import { DateTime } from "luxon";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4, NIL as uuidNull } from "uuid";
import { DocumentKey, IDocumentUpdateFormProps } from "../../../../@types/documents";
import { Button } from "../../../../components/buttons/button";
import { Accordion } from "../../../../components/controls/accordion";
import { DictionaryInput } from "../../../../components/controls/dictionaryInput";
import GridSelectorInput from "../../../../components/controls/GridSelectorInput";
import GridWrapper from "../../../../components/controls/GridWrapper";
import { DateInput, TextInput } from "../../../../components/controls/inputs";
import BaseDocumentCreator from "../../../../components/documents/baseDocumentCreator";
import { IOption, Select } from "../../../../components/selects/select";
import { useTourEducation } from "../../../../components/tours/TourDocument/TourDocumentContext";
import { getInvoiceUpdateFormTourSteps } from "../../../../components/tours/TourDocument/TourSteps/InvoiceUpdateFormTourSteps";
import { ICostInfoDTO, IEntitySimpleDTO } from "../../../../libs/coreapi-dto/@types/common";
import { IGoodsDTO } from "../../../../libs/coreapi-dto/dirs/goods";
import { IInvoiceCreateDTO, IInvoiceGetDTO } from "../../../../libs/coreapi-dto/documents/invoice";
import { ICreateDocumentFromItemsRequest, IInvoiceItemCreateDTO, IInvoiceItemGetDTO } from "../../../../libs/coreapi-dto/documents/invoiceItem";
import { ContractorDataProvider } from "../../../../Services/DataProviders/ContractorDataProvider";
import { GoodsDataProvider } from "../../../../Services/DataProviders/GoodsDataProvider";
import { PricingInvoiceDataProvider } from "../../../../Services/DataProviders/PricingInvoiceDataProvider";
import { useForm } from "../../../../system/hooks/useForm";
import useGridFilter, { DefaultGridFilter, GridFilterAction } from "../../../../system/hooks/useGridFilter";
import { useAppContext } from "../../../../system/providers/appContextProvider";
import { useTabsContext } from "../../../../system/providers/tabsProvider";
import { ContractorSelectorModal } from "../../../Dictionaries/Contractor";
import { GoodsSelectorModal } from "../../../Dictionaries/Goods";
import KizScanModal from "../../../Dictionaries/Kiz/ScanModal/KizScanModal";
import { PricingModelSelectorModal } from "../../../Dictionaries/PricingModel";
import { IKeyValuePair } from "../../../Reports/Shared/Interfaces/IKeyValuePair";
import { InvoiceCreatorItemGridPluginSettings } from "./InvoiceItemCreator/InvoiceItemCreatorGridPluginSettings";
import styles from "../styles/InvoiceCreatorView.module.scss";
import stylesIndex from '../../../styles/index.module.scss'
import { DocumentType, EncodingType, IdTableVariant, KizGoodsGroup, SearchOption, SysOptionName } from "../../../../@types/enumsGlobal";
import InvoiceGoodsSelectModal from "./InvoiceGoodsSelectModal";
import { IGoodsByIdDTO } from "../../../../libs/core-api-requests/dirs/goodsRequest";
import PositionEditPanel from "../../../../components/commandsPanels/PositionEditPanel";
import { MessageModalWindow } from "../../../../components/modalWindows/MessageModalWindow";
import { StockBalanceButtonCompress, StockBalanceButtonExpand } from "../../../../components/buttons/iconButtons/special/QuickAccessBarCollection";
import Tooltip from "../../../../components/tooltips/Tooltip";
import { IScalingRatioDTO } from "../../../../libs/coreapi-dto/dirs/scalingRatio";
import { ScalingRatioDataProvider } from "../../../../Services/DataProviders/ScalingRatioDataProvider";
import { autocomplete } from "../../../../system/functions/autocomplete";
import { getStyleCell } from "../../../../system/functions/getStyleCell";
import { controlKiz, countKizSum } from "../../../../system/functions/controlKiz";
import { ChangeableGrid, IChangeDataGrid } from "../../../../components/grids/changeableGrid/ChangeableGrid";
import collectPricingInvoiceItemDTO, { ICollectPricingInvoiceItemProps } from "../CollectPricingInvoiceItemDTO";
import { KizDataProvider } from "../../../../Services/DataProviders/KizDataProvider";
import { BarcodeType, IKizBoxDTO, IKizDTO, IkizDocumentItemGtinDto } from "../../../../libs/coreapi-dto/dirs/kiz";
import renderGlobalAlert from "../../../../system/hooks/useGlobalAlert";
import { addKizOrKizBox } from "../../../../system/functions/addKizOrKizBox";
import { CheckBox } from "../../../../components/controls/checkbox";
import { StoreSelectorPanel } from "../../../../components/storeSelectorPanel";
import { MoneyFormatComponent } from "../../../../components/controls/inputs/BaseInput";
import { GosContractSelectorModal } from "../../../Dictionaries/GosContract/components/GosContractSelectorModal";
import { addValueToArray } from "../../../../system/functions/addValToArray";
import { GosContractDataProvider } from "../../../../Services/DataProviders/GosContractDataProvider";
import { IGosContractDTO, IGosContractGoodsDTO } from "../../../../libs/coreapi-dto/dirs/gosContract";
import { validateDuplicateKizs } from "../../../../system/functions/validateDuplicateKizs";
import { useTimeout } from "../../../../system/hooks/useTimeout";
import { useComPortScanner } from "../../../../system/hooks/useHubConnection";
import FlexRow from "../../../../components/controls/FlexRow";
import useLockingDocuments from "../../../../components/lockDocuments/useLockingDocuments";
import { IContractorDTO } from "../../../../libs/coreapi-dto/dirs/contractor";
import { checkLockStatus } from "../../../CommonHelperFunctions";
import { useUserContext } from "../../../../system/providers/userContextProvider";
import { InvoiceItemsSelectorModal } from "../InvoiceItem/InvoiceItemsSelectorModal";
import { IBaseDocumentItemKizDTO } from "../../../../libs/coreapi-dto/documents/base";
import { GosContractSearchSelectorModal } from "../../../Dictionaries/GosContract/components/GosContractSearchSelectorModal";
import useItemsByGtin from "../../../../system/hooks/useItemsByGtin";
import useAutoSaveDocument from "../../../../system/hooks/useAutoSaveDocument";
import { SysOptionsDataProvider } from "../../../../Services/DataProviders/SysOptionsDataProvider";
import { GosContractGoodsSelectorModal } from "../../../Dictionaries/GosContract/components/GosContractGoodsSelectorModal";
import { checkCapsLock } from "../../../../system/functions/capsLockHelper";
import { useInvoiceItemTabSettings } from "./InvoiceItemCreator/useInvoiceItemTabSettings";
import { TabIndexSettingsButtonModal } from "../../../../components/buttons/iconButtons/tabIndexSettingsButton/TabIndexSettingsButton";
import { IButton } from "../../../../components/modalWindows/BaseModalWindow";
import { KizCategorySelect, KIZ_CATEGORY } from "../../../Dictionaries/Kiz/ScanModal/KizCategorySelectCell";
import { CheckKizResultsModalWindow } from "../../../../components/modalWindows/CheckKizResultsModalWindow";
import { InvoiceDataProvider } from "../../../../Services/DataProviders/InvoiceDataProvider";
import { GtinListInfoDto, GtinInfoDto, GtinStateInfo } from "../../../../@types/kiz";
import { Spinner } from '../../../../components/spiner/Spinner';
import changeableGridStyles from "../../../../components/grids/changeableGrid/Styles/ChangeableGrid.module.scss";
import { InvoiceStatusCell } from "./InvoiceStatusCell";
import { usePolling } from "../../../../system/hooks/usePolling";
import { SerialDataProvider } from "../../../../Services/DataProviders/SerialDataProvider";
import { ISerialDTO } from "../../../../libs/coreapi-dto/dirs/serial";
import { LotDataProvider } from "../../../../Services/DataProviders/LotDataProvider";
import { SupplierDataChecksDataProvider } from "../../../../Services/DataProviders/SupplierDataChecksDataProviders";
import { SupplierDataCheckAction } from "../../../../libs/coreapi-dto/dirs/supplierDataCheck";
import { IGridCellWithWidth } from "../../../../components/grids/default/defaultGrid";
import { ActRevaluationRecalcModal, AdjustmentFactorType, IMarkupOrMarkdownState, PriceAdjustmentMethod, PriceAdjustmentType } from "../../ActRevaluation/ActRevaluationCreator/ActRevaluationRecalcModal";
import { eventPricingInvoiceTypes } from "../CollectPricingInvoiceDTO";
import { IInvoiceItem, IPricingInvoiceDTO, IPricingInvoiceViewDTO } from "../../../../libs/coreapi-dto/dirs/pricingInvoice";
import { AlertCell } from "../../../../components/grids/changeableGrid/components/AlertCell";
import { IGridStyleOptions, isKizCountValid } from "../../../../components/grids/changeableGrid/utils/gridStyles";
import { getItemsWithErrors, IAlert, updateAlert } from "../../../../components/alert/alertHelpers";
import { useScrollToElement } from "../../../../system/hooks/useScrollToElement";

interface ISum {
    amountRetailSum: number;
    amountRetailVatSum: number;
    amountRetailSumIncVat: number;
    amountSupplierSum: number;
    amountSupplierVatSum: number;
    amountSupplierSumIncVat: number;
}

interface IGoodsViewItem {
    item: IGoodsByIdDTO[] | null;
    show: boolean;
}

interface IStoreModel {
    store?: IEntitySimpleDTO;
}

interface IValidator {
    supplier: IEntitySimpleDTO;
    pricingModel: IEntitySimpleDTO;
    documentDate: DateTime;
}

interface IMessageModalProps {
    show: boolean;
    message: string;
    btSaveOrOk?: BtSaveOrOk;
}

interface IGosContractGoodsModal extends IMessageModalProps {
    entity?: IEntitySimpleDTO
    referentPrice?: boolean;
    gosContractGoods?: IGosContractGoodsDTO[];
    idGosContractGlobal?: string
    idGoodsGlobal?: string
}

interface IGosContractModal extends IMessageModalProps {
    entity?: IEntitySimpleDTO
}

export type BtSaveOrOk = "ok" | "save";

export interface IDisplayItem {
    lineStatus?: 'blue' | 'green' | 'red' | 'yellow';
    idGlobal: string;
    idGoodsGlobal?: string;
    goodsName: string;
    scalingRatioName: string;
    quantity: number;

    productMargin: number;
    producerPrice: number;
    registerPrice: number;

    retailPrice: number;
    retailPriceIncVat: number;
    retailAmount: number;
    retailAmountIncVat: number;
    retailVat: number;

    supplierPrice: number;
    supplierPriceIncVat?: number;
    supplierAmount: number;
    supplierAmountIncVat: number;
    gtin?: string;
    supplierVat: number;

    isKiz: boolean;
    isGnvls: boolean;
    countKizScan: number | undefined;
    numerator?: number;
    denominator?: number;
    goodsCode: string;
    producer: string;

    absentInGC?: boolean | null;
    idInvoiceItemGroupGlobal?: string | null;
    dateCreated: DateTime;
    seriesNumber: string;
    bestBefore: DateTime;
    isOsu?: boolean;
    actualQuantity?: number,
    damageQuantity?: number,
    defectQuantity?: number,
    shortageQuantity?: number,
    storePlaceName?: string
    kizCategory?: string;
    alerts?: IAlert[];
    financialSecurityCode?: string;
}

interface IGoodsViewItem {
    item: IGoodsByIdDTO[] | null;
    show: boolean;
}

export interface IGosContractFields {
    isGosContract: boolean;    
    referentPrice?: boolean;
    gosContractGoods: IGosContractGoodsDTO | null;
    idGosContractGlobal: string | null
}

interface IErrors {
    itemName?: string;
    producerPrice: number;
    registerPrice: number;
    message: string;
}

const InvoiceCreatorForm: FC<IDocumentUpdateFormProps<IInvoiceGetDTO, IInvoiceItemGetDTO[], IInvoiceCreateDTO>> = (props) => {
    const appContext = useAppContext();
    const tabsContext = useTabsContext();
    const userContext = useUserContext();
    const { registerTour } = useTourEducation();

    const lockFromPermission = checkLockStatus(props.permission as IPermission, userContext.userPermission);
    const [onlyRead, setOnlyRead] = useState((props.variant === 'edit' && (props.document?.documentState === 'proc' || lockFromPermission)) ? true : false);

    const idTable = IdTableVariant.Invoice
    const documentType = DocumentType.invoice
    const itemsByGtin = useItemsByGtin(idTable,documentType)
    const itemsDocumentByGtin = itemsByGtin.getKizIdItemsByGtin();

    const { t } = useTranslation();
    const baseT = (value: string) => t("documents.invoice." + value);
    const errorsT = (value: string) => t("errors." + value);
    const checkKizErrorsT = (value: string) => t("documents.invoice.checkKizErrors." + value);



    const goodsDP = new GoodsDataProvider(appContext.coreApiService);
    const contractorDP = new ContractorDataProvider(appContext.coreApiService);
    const kizDP = new KizDataProvider(appContext.coreApiService);
    const pricingInvoiceDP = new PricingInvoiceDataProvider(appContext.coreApiService);
    const scalingRatioDP = new ScalingRatioDataProvider(appContext.coreApiService);
    const gosContractDP = new GosContractDataProvider(appContext.coreApiService);
    const contractorDataProvider = new ContractorDataProvider(appContext.coreApiService);
    const sysOptionsDataProvider = new SysOptionsDataProvider(appContext.coreApiService);
    const sdp = new SerialDataProvider(appContext.coreApiService)
    const idp = new InvoiceDataProvider(appContext.coreApiService);
    const lotDataProvider = new LotDataProvider(appContext.coreApiService)
    const supplierDataCheckDP = new SupplierDataChecksDataProvider(appContext.coreApiService);
    const { pollMultipleTasks } = usePolling();

    InvoiceCreatorItemGridPluginSettings().columns.filter(x=> x.propertyName === 'quantity').forEach((x) => {
        x.changeable = !props.document?.isHardUnProcess
    })

    const lockingDocuments = useLockingDocuments();
    const [idLockingDocument, setIdLockingDocument] = useState<string|undefined>();

    const [data, setData] = useState<IInvoiceCreateDTO>(
        props.documentUpdate ??
            ({
                idGlobal: uuidv4(),
                documentDate: props.isTest ? DateTime.now().plus({ days: -7 }) : DateTime.now(),
            } as IInvoiceCreateDTO)
    );
    
    const [displayItems, setDisplayItems] = useState<IDisplayItem[]>([]);

    useEffect(() => {
        if (!data.items) return;

        setData(prev => {
            if (!prev.items) return prev;

            let hasChanges = false;
            const items = prev.items.map(item => {
                const actualQuantity = item.quantity
                    - ((item.damageQuantity ?? 0) + (item.defectQuantity ?? 0) + (item.shortageQuantity ?? 0));

                if (item.actualQuantity === actualQuantity) return item;

                hasChanges = true;
                return { ...item, actualQuantity };
            });

            return hasChanges ? { ...prev, items } : prev;
        });

        const itemsById = new Map(data.items.map(item => [item.idGlobal, item]));
        setDisplayItems(prev => {
            let hasChanges = false;
            const items = prev.map(item => {
                const sourceItem = itemsById.get(item.idGlobal);
                if (!sourceItem) return item;

                const actualQuantity = sourceItem.quantity
                    - ((sourceItem.damageQuantity ?? 0) + (sourceItem.defectQuantity ?? 0) + (sourceItem.shortageQuantity ?? 0));

                if (
                    item.quantity === sourceItem.quantity &&
                    item.damageQuantity === sourceItem.damageQuantity &&
                    item.defectQuantity === sourceItem.defectQuantity &&
                    item.shortageQuantity === sourceItem.shortageQuantity &&
                    item.actualQuantity === actualQuantity
                ) {
                    return item;
                }

                hasChanges = true;
                return {
                    ...item,
                    quantity: sourceItem.quantity,
                    damageQuantity: sourceItem.damageQuantity,
                    defectQuantity: sourceItem.defectQuantity,
                    shortageQuantity: sourceItem.shortageQuantity,
                    actualQuantity,
                };
            });

            return hasChanges ? items : prev;
        });
    }, [data.items]);

    const selectedItemRef = useRef<IGridRow>();
    const [selectedItem, setSelectedItem] = useState<IGridRow>();
    const [multipleSelect, setMultipleSelect] = useState(false);
    const [selectedItems, setSelectedItems] = useState<IGridRow[]>([]);
    const [isCopyingPositions, setIsCopyingPositions] = useState(false);
    const itemCalculationVersions = useRef(new Map<string, number>());
    const positionIds = new Set((multipleSelect ? selectedItems : selectedItem ? [selectedItem] : [])
        .map(item => item.idGlobal).filter(id => data.items.some(item => item.idGlobal === id)));
    const [duplicateKizItemId, setDuplicateKizItemId] = useState<string>();
    const [invoiceItemWithErrorId, setInvoiceItemWithErrorId] = useState<string>();
    const duplicateKizSelectTimerRef = useRef<number>();
    const [selectedItemCreateDto, setSelectedItemCreateDto] = useState<IInvoiceItemCreateDTO>();
    const [latestPriceOptions, setLatestPriceOptions] = useState<IOption[]>([]);
    const [selectedLatestPriceOption, setSelectedLatestPriceOption] = useState<IOption>();

    const [gridFilter, dispatchGridFilter] = useGridFilter();
    const [averageMarkup, setAverageMarkup] = useState<string>(recalculateAverageMarkup(data.items)); // средняя наценка
    const [mnemocode] = useState<string>(props.document?.mnemocode ?? "");
    const [sum, setSum] = useState<ISum>({
        amountRetailSum: 0,
        amountRetailVatSum: 0,
        amountRetailSumIncVat: 0,
        amountSupplierSum: 0,
        amountSupplierSumIncVat: 0,
        amountSupplierVatSum: 0,
    });
    const [showKizScan, setShowKizScan] = useState<boolean>(false);
    const [showMessageModal, setShowMessageModal] = useState<IMessageModalProps>({ show: false, message: "" });
    const [showMessageModalValidDocument, setShowMessageModalValidDocument] = useState<IMessageModalProps>({ show: false, message: "" });
    const [showMessageModalGosContractGoods, setShowMessageModalGosContractGoods] = useState<IGosContractGoodsModal>({ show: false, message: "", });
    const [showMessageFillByGosContractGoods, setShowMessageFillByGosContractGoods] = useState<IGosContractModal>({ show: false, message: "" });
    const [showGosContractSelectorModal, setShowGosContractSelectorModal] = useState<boolean>(false);
    const [showInvoiceItemSelectorModal, setShowInvoiceItemSelectorModal] = useState<boolean>(false);
    const defaultSearchOption = {displayName: baseT("name"), value: "name"} as IOption;
    const [searchOption, setSearchOption] = useState<IOption>(defaultSearchOption);

	const [isCheckingKiz, setIsCheckingKiz] = useState(false);
	const [showResultsModal, setShowResultsModal] = useState(false);
	const [isRemoving, setIsRemoving] = useState(false);
	const [pollingIntervals, setPollingIntervals] = useState<NodeJS.Timeout[]>([]);

    const [goodsViewItem, setGoodsViewItem] = useState<IGoodsViewItem>({ item: null, show: false });

    const [isCompact, setIsCompact] = useState<boolean>(false);
    const [isCompactStore, setIsCompactStore] = useState<boolean>(false);

    const timeout = useRef<NodeJS.Timeout>();
    const currentIdTab = useRef<string>('');
    const formRef = useRef<HTMLDivElement>(null);

    // Refs для target-элементов tour формы ПН.
    const invoiceMainFieldsTourRef = useRef<HTMLDivElement>(null);
    const invoicePricingModelTourRef = useRef<HTMLDivElement>(null);
    const invoiceAccompanyingDocumentsTourRef = useRef<HTMLDivElement>(null);
    const invoiceGoodsSelectionTourRef = useRef<HTMLDivElement>(null);
    const invoicePositionEditPanelTourRef = useRef<HTMLDivElement>(null);
    const invoicePricesAccordionTourRef = useRef<HTMLDivElement>(null);
    const invoiceActionButtonsTourRef = useRef<HTMLDivElement>(null);
    const invoiceSearchOptionTourRef = useRef<HTMLDivElement>(null);
    const invoiceItemsGridTourRef = useRef<HTMLDivElement>(null);
    const [setTimer, clearTimer]  = useTimeout()

    const [store, setStore] = useState<IEntitySimpleDTO | null>(props.document?.store ?? null);
    const [pharmacy] = useState<IEntitySimpleDTO | null>(null);
    const [pricingModel, setPricingModel] = useState<IEntitySimpleDTO | null>(props.document?.pricingModel ?? null);
    const [supplier, setSupplier] = useState<IEntitySimpleDTO | null>(props.document?.supplier ?? null);
    const [supplierDataCheckAction, setSupplierDataCheckAction] = useState<SupplierDataCheckAction>('NONE')
    //Validations
    const [itemsValidation, setItemsValidation] = useState<IValidationItem[]>([]);
    const [itemsFilter, setItemsFilter] = useState<IItemFilter[]>([]);
    const hasNegativeActualQuantity = data.items?.some(item =>
        item.quantity
        - (item.damageQuantity ?? 0)
        - (item.defectQuantity ?? 0)
        - (item.shortageQuantity ?? 0) < 0
    ) ?? false;

    const [sysOptionsKizValidationType, setSysOptionsKizValidationType] = useState<string>();

    const [currentSupplier, setCurrentSupplier] = useState<IContractorDTO>();
    const [currentPharmacy, setCurrentPharmacy] = useState<IContractorDTO>();
    const [kizScanModal, setKizScanModal] = useState(<></>);
    const [recalcModal, setRecalcModal] = useState(<></>);

    const [gosContract, setGosContract] = useState<IEntitySimpleDTO | null>(null);
    const gosContractSupplierRequestVersion = useRef(0);
    const { showSettings, SettingsModal, getBooleanSetting } = useInvoiceItemTabSettings();

    // Собираем сценарий tour формы ПН из отдельного файла шагов.
    const invoiceCreatorTourSteps = useMemo(() => getInvoiceUpdateFormTourSteps({
        mainFields: invoiceMainFieldsTourRef,
        pricingModel: invoicePricingModelTourRef,
        accompanyingDocuments: invoiceAccompanyingDocumentsTourRef,
        goodsSelection: invoiceGoodsSelectionTourRef,
        positionEditPanel: invoicePositionEditPanelTourRef,
        pricesAccordion: invoicePricesAccordionTourRef,
        actionButtons: invoiceActionButtonsTourRef,
        searchOption: invoiceSearchOptionTourRef,
        itemsGrid: invoiceItemsGridTourRef,
    }), []);

    const scrollToGrid = useScrollToElement({
        containerRef: formRef,
        elementSelector: '[data-grid="true"]',
        offset: 0,
        behavior: 'smooth',
        enabled: true
    });

    useEffect(() => {
        if (!invoiceItemWithErrorId) return;
        if (displayItems.length === 0) return;

        const timer = setTimeout(() => {
            scrollToGrid();
        }, 300);

        return () => clearTimeout(timer);
    }, [invoiceItemWithErrorId, displayItems.length, scrollToGrid]);
    
    const hasItemGosContract = (items: IInvoiceItemCreateDTO[]) => {
        if (!Array.isArray(items)) {
            return false;
        }
        return items.some(item => item?.idGosContractGoodsExternalGlobal);
    }
    const disabledGosContract = hasItemGosContract(data.items) && getBooleanSetting('disabledFieldForGC')
    const showLastSearchQuery = getBooleanSetting('showLastSearchQuery')
    const allowFillByGosContract = getBooleanSetting('allowFillByGosContract')
    const useLotWinnerAsInvoiceSupplier = getBooleanSetting('useLotWinnerAsInvoiceSupplier');
    useEffect(() => {
        gosContractSupplierRequestVersion.current++;
    }, [useLotWinnerAsInvoiceSupplier]);
    const coloredRowIsGnvls = getBooleanSetting('coloredRowIsGnvls')

    const [checkResults, setCheckResults] = useState<GtinInfoDto[] | null>(null);
    
    const lastSearchQuerySettings = {
        showLastSearchQuery: showLastSearchQuery,
        searchOption: searchOption,
        documentKey: 'invoiceItemCreator' as DocumentKey,
        fieldName: 'inputAccompanyingSelectionGoods'
    };


    const [errorSelectGoods, setErrorSelectGoods] = useState<string | undefined>();

    const calculateLineStatus = (item: IInvoiceItemCreateDTO): 'blue' | 'green' | 'red' | 'yellow' => {
    let totalScanned = (item.kizs?.length || 0);
    
    if (item.kizBoxes && item.kizBoxes.length > 0) {
        item.kizBoxes.forEach(box => {
            totalScanned += (box.quantity || 1);
        });
    }
    
    const expected = item.quantity;
    
    if (totalScanned === 0) {
        return 'blue';
    }
    
    if (totalScanned < expected) {
        return 'yellow';
    }
    
    const kizStatuses = [
        ...(item.kizs?.map(k => k.stateMDLP) || []),
        ...(item.kizBoxes?.map(b => b.stateMDLP) || [])
    ];
    
    if (kizStatuses.length > 0) {
        const hasErrors = kizStatuses.some(s => 
            s === "ErrorDateExpired" ||
            s === "ErrorBranchId" ||
            s === "ErrorInCirculation" ||
            s === "ErrorNotFound" ||
            s === "ErrorSscc"
        );
        
        const allOk = kizStatuses.every(s => 
            s === "Ok" || s === "in_circulation" || s === "in_realization"
        );
        
        if (hasErrors) return 'red';
        if (allOk) return 'green';
    }
    
    if (totalScanned >= expected) {
        if (checkResults && checkResults.length > 0) {
            const itemResults = checkResults.filter(r => r.gtin === item.gtin);
            if (itemResults.length > 0) {
                const hasErrors = itemResults.some(r => r.state !== "Ok");
                if (hasErrors) return 'red';
                return 'green';
            }
        }
        return 'green';
    }
    
    return 'blue';
    };

   const onCheckSgtinsAction = async () => {
    if (!data.idGlobal) {
        renderGlobalAlert({
            variant: "error",
            statusCode: 400,
            title: baseT("checkKizError"),
            detail: checkKizErrorsT("documentNotSaved")
        });
        return;
    }

    setIsCheckingKiz(true);
    
    try {
        const documentId = data.idGlobal;
        const taskIds = await new Promise<string[]>((resolve, reject) => {
            idp.checkSgtins(documentId,
                (response) => {
                    console.log("checkSgtins response:", response);
                    if (response && response.taskIds) {
                        resolve(response.taskIds);
                    } else if (Array.isArray(response)) {
                        resolve(response);
                    } else {
                        reject({ message: "Не получен список taskIds" });
                    }
                },
                (error) => {
                    console.error("checkSgtins error:", error);
                    reject(error);
                }
            );
        });
        
        if (!taskIds || taskIds.length === 0) {
            renderGlobalAlert({
                variant: "warning",
                statusCode: 400,
                title: "Внимание",
                detail: checkKizErrorsT("noTasks")
            });
            setIsCheckingKiz(false);
            return;
        }
        
        const allResults = await pollMultipleTasks(
            taskIds,
            async (taskId: string) => {
                return new Promise((resolve, reject) => {
                idp.getCheckSgtinsResult(
                    taskId, 
                    data.idGlobal!,
                    (response) => {
                        resolve(response);
                    },
                    (error) => {
                        reject(error);
                    }
                );
            });
            },
        {
            maxAttempts: 60,
            maxErrorAttempts: 10,
            intervalMs: 5000,
            timeoutMs: 300000,
            errorMessages: {
            maxAttemptsError: checkKizErrorsT("maxAttemptsError"),
            pollingTimeout: checkKizErrorsT("pollingTimeout"),
            taskNotFound: checkKizErrorsT("taskNotFound")
        }
        });
        

        const allGtinInfo = allResults.flatMap(r => r?.gtinInfoList || []);        
        setCheckResults(allGtinInfo);
        
        const updatedDisplayItems = displayItems.map(item => {
            const itemData = data.items.find(i => i.idGlobal === item.idGlobal);
            return {
                ...item,
                lineStatus: itemData ? calculateLineStatus(itemData) : 'blue'
            };
        });
        setDisplayItems(updatedDisplayItems);
        
        const errorResults = allGtinInfo.filter(r => r.state !== "Ok");
        
        if (errorResults.length === 0) {
            renderGlobalAlert({
                variant: "success",
                statusCode: 200,
                title: baseT("checkKizSuccess"),
                detail: baseT("checkKizSuccess")
            });
            props.refresh?.();
        } else {
            setShowResultsModal(true);
        }
        
    } catch (err) {
    const error = err as any;

    let errorMessage = "";
    if (typeof error?.message === 'string') {
        errorMessage = error.message;
    } else if (error?.message?.title) {
        errorMessage = error.message.title;
    } else if (error?.title) {
        errorMessage = error.title;
    } else {
        errorMessage = JSON.stringify(error?.message || error);
    }
    
    const isBusinessError = errorMessage.includes("Нет КИЗов") || 
                            errorMessage.includes("Нет КИЗов для проверки");
    
    if (isBusinessError) {
        renderGlobalAlert({
            variant: "success",
            statusCode: 200,
            title: checkKizErrorsT("noKizs"),
            detail: checkKizErrorsT("noKizsDetail")
        });
    } else {
        renderGlobalAlert({
            variant: "error",
            statusCode: error?.statusCode || error?.message?.statusCode || 500,
            title: baseT("checkKizError"),
            detail: errorMessage || checkKizErrorsT("unknownError")
        });
    }
    } finally {
        setIsCheckingKiz(false);
    }
    };

    const handleRemoveInvalidKizs = async (invalidSgtins: string[]) => {
    
        if (!data.idGlobal) {
            renderGlobalAlert({
                variant: "error",
                statusCode: 400,
                title: "Ошибка",
                detail: "ID документа не найден"
            });
            return;
        }
    
        if (!invalidSgtins || invalidSgtins.length === 0) {
            setShowResultsModal(false);
            return;
        }
    
        setIsRemoving(true);
        
        const updatedItems = data.items.map(item => {
            const remainingKizs = item.kizs?.filter(kiz => {
                const shouldKeep = !invalidSgtins.includes(kiz.gtinSGtin);
                if (!shouldKeep) {
                    console.log(`>>> Удаляем КИЗ: ${kiz.gtinSGtin} из позиции ${item.idGlobal}`);
                }
                return shouldKeep;
        }) || [];
        
        const remainingKizBoxes = item.kizBoxes?.filter(box => {
            const shouldKeep = !invalidSgtins.includes(box.barcode);
            if (!shouldKeep) {
                console.log(`>>> Удаляем короб: ${box.barcode} из позиции ${item.idGlobal}`);
            }
            return shouldKeep;
        }) || [];
        
        const removedKizCount = (item.kizs?.length || 0) - remainingKizs.length;
        const removedBoxCount = (item.kizBoxes?.length || 0) - remainingKizBoxes.length;
        const removedCount = removedKizCount + removedBoxCount;
        
        if (removedCount === 0) return item;
        
        console.log(`Позиция ${item.idGlobal}: удалено ${removedCount} (КИЗ: ${removedKizCount}, коробов: ${removedBoxCount})`);
        
        return {
            ...item,
            quantity: item.quantity,
            actualQuantity: (item.actualQuantity || item.quantity),
            kizs: remainingKizs,
            kizBoxes: remainingKizBoxes,
            kizCount: remainingKizs.length,
            kizBoxCount: remainingKizBoxes.length
        };
        });
    
        const updatedDisplayItems = displayItems.map(item => {
            const removedCount = invalidSgtins.filter(sgtin => item.gtin === sgtin).length;
            if (removedCount === 0) return item;
                
        return {
            ...item,
            countKizScan: (item.countKizScan || 0),
            quantity: item.quantity,
            actualQuantity: (item.actualQuantity || item.quantity)
        };
        });
        
        setData({ ...data, items: updatedItems });
        setDisplayItems(updatedDisplayItems);
        setShowResultsModal(false);
        setCheckResults(null);
    
        renderGlobalAlert({
            variant: "success",
            statusCode: 200,
            title: "КИЗы удалены",
            detail: `Удалено ${invalidSgtins.length} проблемных КИЗов`
        });
    
        setIsRemoving(false);
    
        if (props.save) {
            props.save({ ...data, items: updatedItems });
        }
    
    };
    
    const checkKiz: IButton =
        {
            title: baseT("checkKiz"),
            disabled: !data.items || data.items.length === 0 || onlyRead || props.isSubmitting,
            onClick: onCheckSgtinsAction
        };

    const otherActions: IButton[] = [
        {
            title: baseT("createByGosContract"),
            disabled: (data.items && data.items.length > 0 ) || !allowFillByGosContract,
            onClick: () => setShowGosContractSelectorModal(true)
        },
        {
            title: baseT("createReturnActForProblematicRows"),
            disabled: props.document?.documentState !== 'proc' || props.variant !== 'edit',
            onClick: () => setShowInvoiceItemSelectorModal(true)
        }
    ];

    const gosContractMap = useMemo(() => {
        const map = new Map<string, boolean>();
        
        data?.items?.forEach((item: IInvoiceItemCreateDTO) => {
            const hasGosContract = 
                item.idGosContractGoodsExternalGlobal !== undefined &&
                item.idGosContractGoodsExternalGlobal !== null &&
                item.idGosContractGoodsExternalGlobal !== '';
            
            map.set(item.idGlobal, hasGosContract);
        });
        
        return map;
    }, [data?.items]);

    const firstUpdate = useRef(true);
    useEffect(() => {
        if (firstUpdate.current) {
            firstUpdate.current = false;
          } else {
            if (props.documentUpdate)
            setData(props.documentUpdate)
          }
    },[props.documentUpdate])

    useEffect(() => setData({ ...data, idStoreGlobal: store?.idGlobal }), [store]);
    useEffect(() => setData({ ...data, idPricingModelGlobal: pricingModel?.idGlobal }), [pricingModel]);
    useEffect(() => setData({ ...data, idSupplierGlobal: supplier?.idGlobal }), [supplier]);


    useEffect(() => setStore(props.document?.store ? { ...props.document?.store } : null), [props.document?.store]);
    useEffect(() => setPricingModel(props.document?.pricingModel ? { ...props.document?.pricingModel } : null), [props.document?.pricingModel]);

    useEffect(() => {
        if (props.document?.gosContract) setGosContract(props.document.gosContract);
    }, [props.document?.gosContract]);

    useEffect(() => {
        if (!data.idPricingModelGlobal) return;
    }, [data.idPricingModelGlobal]);


    useEffect(() => {
        if (supplier) {
            return;
        }

        if (props.document?.supplier) {
            setSupplier({ ...props.document?.supplier });
        } else {
            autocomplete().then((e) => {
                e.defaultSupplier && setSupplier({ idGlobal: e.defaultSupplier.idGlobal, displayName: e.defaultSupplier.name });
            });
        }
    }, [props.document?.supplier]);

    useEffect(() => {
        if (pricingModel !== null) {
            return;
        }
        if (pharmacy !== null) {
            contractorDP.getById(pharmacy.idGlobal, (contractor) => {
                setPricingModel(contractor.pricingPricingModel);
                setCurrentPharmacy(contractor);
            });
        }
    }, [pharmacy]);

    useEffect(() => {
        if (supplier) {
            contractorDataProvider.getById(supplier.idGlobal, (dataRequest) => setCurrentSupplier(dataRequest));
        }
    }, [supplier])

    useEffect(() => {
        const handleTabClose = (event) => {
            lockingDocuments.delete({
                idTable: idTable,
                idDocument: data.idGlobal as string,
            });
            return (event.returnValue = "");
        };

        window.addEventListener("beforeunload", handleTabClose);

        return () => {
            window.removeEventListener("beforeunload", handleTabClose);
        };
    }, []);

    //обновление id для фокусировки на товаре с ошибкой
    useEffect(() => {
        if (!props.focusItemId) {
            return;
        }
        setInvoiceItemWithErrorId(props.focusItemId);
    }, [props.focusItemId]);

    // Блокировка документа
    const requestUpdateLockingDocumentTimeout = useRef<NodeJS.Timeout>();
    const isUpdatingLockingDocument = useRef(false);

    useEffect(() => {
        // Если документ не определён — ничего не делаем
        if (!idLockingDocument) return;

        // Если уже идёт обновление — не ставим новый таймер
        if (isUpdatingLockingDocument.current) return;

        // Отменяем предыдущий таймер
        if (requestUpdateLockingDocumentTimeout.current) {
            clearTimeout(requestUpdateLockingDocumentTimeout.current);
        }

        clearTimeout(requestUpdateLockingDocumentTimeout.current as NodeJS.Timeout);
        requestUpdateLockingDocumentTimeout.current = setTimeout(() => {

            isUpdatingLockingDocument.current = true;

            lockingDocuments.update(idLockingDocument, (e) => {
                isUpdatingLockingDocument.current = false;
            },
                () => {
                    isUpdatingLockingDocument.current = false;
                })
        }, Number(process.env.REACT_APP_INTERVAL_REQUEST));
    }, [data.documentDate, data.idPricingModelGlobal, data.idStoreGlobal, data.idSupplierGlobal, data.items]);

    useEffect(() => {
        if (tabsContext?.currentTab) {
            currentIdTab.current = tabsContext.currentTab?.id;
        }
    }, []);

    // Регистрируем tour, чтобы кнопка обучения в QuickAccessBar стала активной.
    useEffect(() => {
        return registerTour({
            plugin: "invoice_creator_plugin",
            tabId: currentIdTab.current,
            steps: invoiceCreatorTourSteps,
        });
    }, [registerTour, invoiceCreatorTourSteps]);

    //загрузка настроек контроля данных поставщика
    const loadSupplierDataCheckActionSettings = (): Promise<SupplierDataCheckAction> => {
        return new Promise((resolve) => {
            const filter = {
                ...DefaultGridFilter,
                columnFilters: [
                    {
                        name: "mnemocode",
                        operator: "Eq",
                        value: "PRODUCER_PRICE_GT_REGISTER_PRICE_4_IMPORTANT_GOODS"
                    } as IGridColumnFilter
                ]
            };

            supplierDataCheckDP.getView(filter, (data) => {
                const action = data?.[0]?.action as SupplierDataCheckAction || "NONE";
                setSupplierDataCheckAction(action);
                resolve(action);
            });
        });
    };

    useEffect(() => {
        loadSupplierDataCheckActionSettings()
    }, [displayItems])

     //обновление отображения ошибок
    const refreshDisplayItemAlerts = () => {
        const options: IGridStyleOptions = {
            documentType,
            sysOptionsKizValidationType,
            getIsLs: item =>
                data.items?.find(x => x.idGlobal === item.idGlobal)?.isLs ?? false
        };

        setDisplayItems(prev =>
            prev.map(displayItem => {

                let item = displayItem;

                item = updateAlert(
                    item,
                    {
                        field: ["producerPrice"],
                        message: t("documents.invoice.exceedingProducerPrice"),
                    },
                    supplierDataCheckAction !== "NONE" &&
                    displayItem.isGnvls &&
                    displayItem.producerPrice != null &&
                    displayItem.registerPrice != null &&
                    displayItem.producerPrice > displayItem.registerPrice
                );

                item = updateAlert(
                    item,
                    {
                        field: ["kizCount"],
                        message: t("documents.invoice.messages.kizCountNotMatch")
                    },
                    !!displayItem && !isKizCountValid(displayItem, options)
                );

                return item;
            })
        );
    };

    useEffect(() => {
        refreshDisplayItemAlerts();
    }, [
        data.items,
        supplierDataCheckAction,
        documentType,
        sysOptionsKizValidationType
    ]);

    const itemsWithErrors = getItemsWithErrors(displayItems);

    useComPortScanner((scanData) => {
        if (tabsContext.currentTab && (tabsContext.currentTab?.id === currentIdTab.current)) {
            if (!showKizScan) {
                const decodedText = atob(scanData);
                handleEntitySelect(undefined, decodedText)
            }
        }
    })
    
    const { isValid, errors, setErrors } = useForm<IStoreModel & IValidator>({
        validations: {
            supplier: {
                required: {
                    value: data?.idSupplierGlobal ? false : true,
                    message: errorsT("required"),
                },
            },
            store: {
                required: {
                    value: data?.idStoreGlobal ? false : true,
                    message: errorsT("required"),
                },
            },
            pricingModel: {
                required: {
                    value: data?.idPricingModelGlobal ? false : true,
                    message: errorsT("required"),
                },
            },
            documentDate: {
                required: {
                    value: data?.documentDate?.day ? false : true,
                    message: errorsT("required"),
                },
            },
        },
    });

    useEffect(() => {
        const selectedItemDto = selectedItem
            ? data.items.find((x) => x.idGlobal === selectedItem.idGlobal)
            : undefined;

        setSelectedItemCreateDto(selectedItemDto);
        setLatestPriceOptions([]);
        setSelectedLatestPriceOption(undefined);

        if (!selectedItem?.idGoodsGlobal) {
            return;
        }

        const abortController = new AbortController();

        lotDataProvider.getLatestPrices(
            {
                ...DefaultGridFilter,
                numberPerPage: 100,
                paramFilter: {idGoodsGlobal: selectedItem.idGoodsGlobal},
            },
            (latestPrices) => {
                const options = latestPrices.map<IOption>((latestPrice) => ({
                    displayName: String(latestPrice.retailPriceIncVat),
                    value: latestPrice.idLotGlobal,
                }));

                setLatestPriceOptions(options);
                setSelectedLatestPriceOption(options[0]);
            },
            abortController.signal,
        );

        return () => abortController.abort();
    }, [selectedItem]);

    useEffect(() => {
        if (data?.items) {
            setAverageMarkup(recalculateAverageMarkup(data.items));
            setSum({
                ...sum,
                amountRetailSum: data.items.reduce((sum, item) => sum + item.retailCostInfo.sum, 0),
                amountRetailVatSum: data.items.reduce((sum, item) => sum + item.retailCostInfo.vatSum, 0),
                amountRetailSumIncVat: data.items.reduce((sum, item) => sum + item.retailCostInfo.sumIncVat, 0),
                amountSupplierSum: data.items.reduce((sum, item) => sum + item.supplierCostInfo.sum, 0),
                amountSupplierVatSum: data.items.reduce((sum, item) => sum + item.supplierCostInfo.vatSum, 0),
                amountSupplierSumIncVat: data.items.reduce((sum, item) => sum + item.supplierCostInfo.sumIncVat, 0),
            });
        }
    }, [data?.items,displayItems]);

    const addItemsFilter = (items = props.document?.items) => {
        let arr: IItemFilter[] = [];
        items?.forEach((x) => {
            arr.push({
                idItem: x.idGlobal as string,
                quantityMax: 9999999, // TODO: magic number fix
                quantityMin: 0,
            });
        });
        setItemsFilter(arr);
    };

    useEffect(() => {
        if ((props.variant === "edit" || props.variant === "copy") && props.document) {
            if (props.variant === "edit")
                lockingDocuments.check(data.idGlobal as string, (e)=> {
                    if (e) setOnlyRead(true);                    
                    else
                        lockingDocuments.send({
                            idTable: idTable,
                            idDocument: data.idGlobal as string,
                        }, (e)=> {
                            setIdLockingDocument(e)
                        });
                })

            addItemsFilter();
            const propsValidateItems = props.document.items.map((el) => ({
                err: false,
                message: "",
                idGlobal: el.idGlobal,
            })) as IValidationItem[];
            setItemsValidation(propsValidateItems);
        }
        sysOptionsDataProvider.getByName(SysOptionName.KizValidationType, (e) => {
            setSysOptionsKizValidationType(e?.value as string);
        });
    }, []);

    useEffect(() => {        
        if (!itemsDocumentByGtin) return
        itemsByGtin.allowKiz(data.items, displayItems, (e) => setKizByGtin(e), (idGlobal) => {
            if (duplicateKizSelectTimerRef.current) {
                window.clearTimeout(duplicateKizSelectTimerRef.current);
            }
            setDuplicateKizItemId(undefined);
            duplicateKizSelectTimerRef.current = window.setTimeout(() => {
                setDuplicateKizItemId(idGlobal);
            }, 0);
        })
    },[itemsDocumentByGtin])

    useEffect(() => {
        return () => {
            if (duplicateKizSelectTimerRef.current) {
                window.clearTimeout(duplicateKizSelectTimerRef.current);
            }
        }
    }, []);

    useEffect(() => {
        if (props.documentItems) {
            setDisplayItems(
                props.documentItems.map((x): IDisplayItem => {
                    const dataItem = data.items.find(item => item.idGlobal === x.idGlobal);
                    const countKizBoxesGisMt = dataItem?.kizBoxesGisMt?.reduce((sum, box) => sum + (box.quantity || 0), 0) ?? 0;
                    const hasKizData = dataItem ?
                        ((dataItem.kizs && dataItem.kizs.length > 0)) :
                        false;

                    const hasKizOsuData = dataItem ?
                        (dataItem.kizOsuDtos && dataItem.kizOsuDtos.length > 0) :
                        false;

                        let kizCategory: 'medicines' | 'gisMt' = KIZ_CATEGORY.medicines;

                    if (hasKizData && !hasKizOsuData) {
                        kizCategory = KIZ_CATEGORY.medicines;
                    } else if (!hasKizData && hasKizOsuData) {
                        kizCategory = KIZ_CATEGORY.gisMt;
                    }

                    return {
                        lineStatus: dataItem ? calculateLineStatus(dataItem) : 'blue',
                        idGlobal: x.idGlobal as string,
                        idGoodsGlobal: x.goods.idGlobal,
                        goodsName: x.goods.displayName,
                        scalingRatioName: x.scalingRatio.displayName,
                        quantity: x.quantity,
                        productMargin: x.retailCostInfo?.adprice ?? x.productMargin,
                        producerPrice: x.producerPrice,
                        registerPrice: x.registerPrice,
                        goodsCode: x.goodsCode,
                        producer: x.producer,
                        retailPrice: x.retailCostInfo.price,
                        retailPriceIncVat: x.retailCostInfo.priceIncVat,

                        retailAmount: x.retailCostInfo.sum,
                        retailAmountIncVat: x.retailCostInfo.sumIncVat,
                        retailVat: x.retailCostInfo.vat,

                        supplierAmount: x.supplierCostInfo.sum,
                        supplierAmountIncVat: x.supplierCostInfo.sumIncVat,
                        gtin: x.gtin,
                        supplierPrice: x.supplierCostInfo.price,
                        supplierPriceIncVat: x.supplierCostInfo.priceIncVat,
                        supplierVat: x.supplierCostInfo.vat,

                        isKiz: x.isKiz || x.isKizBox,
                        countKizScan: (x.isKiz || x.isKizBox) === true ? x.kizBoxCount + x.kizCount + countKizBoxesGisMt : undefined,
                        isGnvls: x.isGnvls ?? false,
                        denominator: x.denominator,
                        numerator: x.numerator,
                        absentInGC: props.documentUpdate?.idGosContractExternalGlobal ? (x.idGosContractGoodsGlobal ? false : true) : null,
                        idInvoiceItemGroupGlobal: x.idInvoiceItemGroupGlobal,
                        dateCreated: x.dateCreated,
                        bestBefore: x.bestBefore,
                        seriesNumber: x.seriesNumber,
                        isOsu: x.kizOsuDto ? true : x.isOsu,
                        actualQuantity: x.quantity - (x.damageQuantity ?? 0) - (x.defectQuantity ?? 0) - (x.shortageQuantity ?? 0),
                        damageQuantity: x.damageQuantity,
                        defectQuantity: x.defectQuantity,
                        shortageQuantity: x.shortageQuantity,
                        storePlaceName: x.storePlace?.displayName,
                        kizCategory: kizCategory,
                        financialSecurityCode: x?.financialSecurity?.displayName
                    };
                })
            );

            const updatedDataItems = data.items.map(item => {
                const documentItem = props.documentItems?.find(docItem => docItem.idGlobal === item.idGlobal);
                if (documentItem) {
                    return {
                        ...item,
                        actualQuantity: documentItem.quantity - (documentItem.damageQuantity ?? 0) - (documentItem.defectQuantity ?? 0) - (documentItem.shortageQuantity ?? 0),
                    };
                }
                return item;
            });
    
            setData({
                ...data,
                items: updatedDataItems
            });
        }
    }, [props.documentItems]);
    
    useEffect(() => {
        if (data.items && data.items.length > 0) {
            setDisplayItems(prev => {
                return prev.map(displayItem => {
                    const updatedItem = data.items.find(item => item.idGlobal === displayItem.idGlobal);
                    if (updatedItem) {
                        const hasKizData = (updatedItem.kizs && updatedItem.kizs.length > 0)
                        const hasKizOsuData = (updatedItem.kizOsuDtos && updatedItem.kizOsuDtos.length > 0);
                        
                        let kizCategory: 'medicines' | 'gisMt' = KIZ_CATEGORY.medicines;
                        if (hasKizData && !hasKizOsuData) {
                            kizCategory = KIZ_CATEGORY.medicines;
                        } else if (!hasKizData && hasKizOsuData) {
                            kizCategory = KIZ_CATEGORY.gisMt;
                        }
                        
                        return {
                            ...displayItem,
                            kizCategory: kizCategory,
                        };
                    }
                    return displayItem;
                });
            });
        }
    }, [data.items]);
    
    const setKizScanComponent = async (gridSelectedItem?: IGridRow, isFocusScanKizBox?: boolean) => {

        // Находим актуальный элемент в data.items по id из gridSelectedItem
        // уменьшает риск гонки за актуальным состоянием, так как нет вторичных зависимостей
        let selectedItem = gridSelectedItem
            ? data.items.find(x => x.idGlobal === gridSelectedItem.idGlobal)
            : selectedItemCreateDto

        if (!selectedItem) {
            setKizScanModal(<></>);
            return;
        }

        try {
            // Запрашиваем ScalingRatio напрямую
            const scalingRatio: IScalingRatioDTO | null = await new Promise<IScalingRatioDTO | null>((resolve) => {

                scalingRatioDP.overrideGetById(
                    selectedItem!.idGoodsGlobal,
                    selectedItem!.idScalingRatioGlobal as string,
                    (result) => resolve(result)
                );
            });

            if (!scalingRatio) {
                setKizScanModal(<></>);
                return;
            }

            setKizScanModal(
                <KizScanModal
                    ok={(value: IInvoiceItemCreateDTO, sr?: IScalingRatioDTO) => {
                        let result = changeItemScalingRatio(value, sr);

                        setData({
                            ...data,
                            items: [...data.items.filter((x) => x.idGlobal !== result.idGlobal), result],
                        });

                        setDisplayItems(
                            displayItems.map((x): IDisplayItem => ({
                                ...x,
                                countKizScan:
                                    x.idGlobal === result.idGlobal
                                        ? countKizSum(result, documentType)
                                        : x.countKizScan,
                                scalingRatioName:
                                    x.idGlobal === result.idGlobal
                                        ? sr?.displayName ?? x.scalingRatioName
                                        : x.scalingRatioName,
                                dateCreated:
                                    x.idGlobal === result.idGlobal ? DateTime.now() : x.dateCreated,
                                isOsu: x.idGlobal === result.idGlobal ? value?.isOsu : x.isOsu,
                            }))
                        );

                        setShowKizScan(false);
                        setKizScanModal(<></>);
                    }}
                    cancel={() => {
                        setShowKizScan(false);
                        setKizScanModal(<></>);
                    }}
                    selectedItem={selectedItem}
                    documentType={documentType}
                    sysOptionsKizValidationType={sysOptionsKizValidationType}
                    document={{
                        idTable: idTable,
                        idDocument: data.idGlobal as string,
                    }}
                    idLotFrom={null}
                    scalingRatio={scalingRatio}
                    numerator={scalingRatio.numerator}
                    idGoodsGlobal={selectedItem.idGoodsGlobal}
                    onValidateDuplicate={(barcode) => validateDuplicateKizs(data.items, barcode)}
                    isFocusScanKizBox={isFocusScanKizBox}
                />
            );
        } catch (error) {
            console.error("Ошибка при загрузке scalingRatio:", error);
            setKizScanModal(<></>);
        }
    };

    function setKizByGtin(e: IBaseDocumentItemKizDTO | undefined) {
        const itemId = e?.idGlobal;
        if (!itemId) return;

        const item = data.items.find(x => x.idGlobal === itemId);
        if (!item) return;

        // Обновляем данные элемента
        setData({
            ...data,
            items: [
                ...data.items.filter(x => x.idGlobal !== itemId),
                {
                    ...item,
                    kizs: e.kizs,
                    kizBoxes: e.kizBoxes,
                    kizOsuDto: e.kizOsuDto,
                    kizOsuDtos: e.kizOsuDtos,
                    kizBoxesGisMt: e.kizBoxesGisMt
                }
            ]
        });

        // Обновляем отображаемый элемент
        const displayItem = displayItems.find(x => x.idGlobal === itemId);
        if (displayItem) {
            setDisplayItems([
                ...displayItems.filter(x => x.idGlobal !== itemId),
                {
                    ...displayItem,
                    countKizScan: (displayItem.countKizScan ?? 0) + 1
                }
            ]);
        }

        renderGlobalAlert({
            variant: "success",
            statusCode: 200,
            title: baseT("kizAddedSuccess"),
            isSound: true
        });

        // Если короб, открываем модалку сканирования
        if (itemsDocumentByGtin?.kizParsedDto.barcodeType === BarcodeType.KizBox) {
            setShowKizScan(true);
            setKizScanComponent(selectedItem, true);
        }
    }

    function recalculateAverageMarkup(items: IInvoiceItemCreateDTO[] | undefined): string {// Получение средней наценки.
        let result: number = 0;
        if (items && items.length) {
            for (let i = 0; i < items.length; i++) {
                result += items[i].productMargin;
            }
            result = result / items.length;
        }
        return result > 0 ? result.toFixed(2).toString() : "0";
    }

    const openTab = (entity: IEntitySimpleDTO, gosContractGoods: IGosContractFields) => {
        tabsContext.openChild(
            "invoice_item_creator_plugin",
            "create",
            undefined,
            (value: IInvoiceItemCreateDTO, displayItem: IDisplayItem) => {
                setData({
                    ...data,
                    items: data.items ? [...data.items?.filter((x) => x.idGlobal !== value.idGlobal), value] : [value],
                });
                setDisplayItems([...displayItems.filter((x) => x.idGlobal !== displayItem.idGlobal), displayItem]);

                setItemsFilter([...itemsFilter, { idItem: value.idGlobal, quantityMax: 9999999, quantityMin: 0 }]);
                setItemsValidation([...itemsValidation, { err: false, message: "", idGlobal: value.idGlobal }]);
            },
            {
                goods: entity,
                idPricingModel: data.idPricingModelGlobal,
                idSupplier: data.idSupplierGlobal,
                idStore: data.idStoreGlobal,
                gosContractFields: gosContractGoods,
                currentSupplier: currentSupplier,
                currentPharmacy: currentPharmacy,
                variant: 'create',
                isUnProcessHard: props.document?.isHardUnProcess,
                documentDate: data.documentDate,
                isGiftInvoice: data.isGiftInvoice
            }
        );
    };

    function callOpenInvoiceItemCreatorPlugin(entity: IEntitySimpleDTO) {
        if (data.idGosContractExternalGlobal) {
            gosContractDP.getById(data.idGosContractExternalGlobal, (gosContract) => {

                //Если entity.idGlobal = GosContractGoodsIdGlobal
                let gosContractGoods = gosContract.goods.filter((x) => x.idGlobal === entity.idGlobal);
                //Если entity.idGlobal = IdGoodsGlobal (Goods.idGlobal)
                if (gosContractGoods.length === 0) {
                    gosContractGoods = gosContract.goods.filter((x) => x.goods?.idGlobal === entity.idGlobal);
                }

                if (gosContractGoods.length === 1) {
                    openTab({...entity, idGlobal: gosContractGoods[0].goods.idGlobal}, { 
                        isGosContract: true, 
                        referentPrice: gosContract.priceInfo.referentPrice, 
                        gosContractGoods: gosContractGoods[0] ?? null,
                        idGosContractGlobal: data.idGosContractExternalGlobal!
                     });
                }
                else if (gosContractGoods.length > 1) {
                    setShowMessageModalGosContractGoods({
                        show: true, 
                        entity: entity, 
                        gosContractGoods:gosContractGoods, 
                        idGoodsGlobal: 
                        entity.idGlobal,
                        idGosContractGlobal: data.idGosContractExternalGlobal,
                        referentPrice: gosContract.priceInfo.referentPrice,
                        message: baseT("choiseGosContractGoods")
                    })
                }
            });
        } else {
            openTab(entity, { isGosContract: false, gosContractGoods: null, idGosContractGlobal: null });
        }
    }

    const calcRetailPriceIncVat = (idGlobal: string, value: number, item: IInvoiceItemCreateDTO) => {
        calcInvoiceItem(
            {
                eventTypes: "RetailPriceWithVatChanged",
                idInvoice: "",
                hasRetailPrice: true,
                idSupplier: data.idSupplierGlobal,
                idStore: data.idStoreGlobal,
                idItem: "",
                idGoods: item.idGoodsGlobal as string,
                idScalingRatio: item.idScalingRatioGlobal,
                quantity: item.quantity,
                actualQuantity: item.actualQuantity,
                damageQuantity: item.damageQuantity,
                defectQuantity: item.defectQuantity,
                shortageQuantity: item.shortageQuantity,
                producerPrice: item.producerPrice,
                retailCostInfo: { ...item.retailCostInfo, priceIncVat: value as number },
                supplierCostInfo: item.supplierCostInfo,
                registerPrice: item.registerPrice,
            },
            idGlobal
        );
    };

    const calcRetailAmount = (idGlobal: string, value: number, item: IInvoiceItemCreateDTO) => {
        calcInvoiceItem(
            {
                eventTypes: "RetailAmountChanged",
                idInvoice: "",
                idSupplier: data.idSupplierGlobal,
                idStore: data.idStoreGlobal,
                idItem: "",
                idGoods: item.idGoodsGlobal as string,
                idScalingRatio: item.idScalingRatioGlobal,
                quantity: item.quantity,
                actualQuantity: item.actualQuantity,
                damageQuantity: item.damageQuantity,
                defectQuantity: item.defectQuantity,
                shortageQuantity: item.shortageQuantity,
                producerPrice: item.producerPrice,
                retailCostInfo: { ...item.retailCostInfo, sum: value as number },
                supplierCostInfo: item.supplierCostInfo,
                registerPrice: item.registerPrice,
            },
            idGlobal
        );
    };

    const calcProductMargin = (idGlobal: string, value: number, item: IInvoiceItemCreateDTO, suppressRetailPriceChangeReasonAlert = false) => {
        calcInvoiceItem(
            {
                eventTypes: "PercentageOfMarkupRetailChanged",
                idInvoice: "",
                idSupplier: data.idSupplierGlobal,
                idStore: data.idStoreGlobal,
                idItem: "",
                idGoods: item.idGoodsGlobal as string,
                idScalingRatio: item.idScalingRatioGlobal,
                quantity: item.quantity,
                actualQuantity: item.actualQuantity,
                damageQuantity: item.damageQuantity,
                defectQuantity: item.defectQuantity,
                shortageQuantity: item.shortageQuantity,
                producerPrice: item.producerPrice,
                retailCostInfo: { ...item.retailCostInfo, adprice: value as number },
                supplierCostInfo: item.supplierCostInfo,
                registerPrice: item.registerPrice,
            },
            idGlobal,
            suppressRetailPriceChangeReasonAlert
        );
    };

    function calcInvoiceItem(collectionProps: ICollectPricingInvoiceItemProps, idGlobal: string, suppressRetailPriceChangeReasonAlert = false) {
        if (data.idPricingModelGlobal === undefined || data.idPricingModelGlobal === null || data.idPricingModelGlobal === uuidNull) {
            renderGlobalAlert({ variant: "error", statusCode: 500, title: baseT("messages.pricingModelNotSelected"), instance: baseT("pricingModelService") });
            return;
        }

        const calculationVersion = (itemCalculationVersions.current.get(idGlobal) ?? 0) + 1;
        itemCalculationVersions.current.set(idGlobal, calculationVersion);
        let pricingInvoiceDTO = collectPricingInvoiceItemDTO(collectionProps);
        pricingInvoiceDP.getCalculatedInvoiceItemViewAsync(data.idPricingModelGlobal as string, pricingInvoiceDTO, (entity) => {
            if (itemCalculationVersions.current.get(idGlobal) !== calculationVersion) return;
            const alertMessage = entity.retailPriceChangeReason ? [
                {
                    field: ["retailPriceIncVat", "productMargin"],
                    message: entity.retailPriceChangeReason || ""
                }
            ] : [];

            setData(currentData => ({
                ...currentData,
                items: currentData.items.map(currentItem => {
                    if (currentItem.idGlobal !== idGlobal) return currentItem;

                    return {
                        ...currentItem,
                        retailCostInfo: {
                            vat: entity.percentageOfVatRateRetail,
                            price: entity.retailPrice,
                            vatPrice: entity.retailVatPerUnit,
                            priceIncVat: entity.retailPriceWithVat,
                            sum: entity.retailAmount,
                            vatSum: entity.vatAmountRetail,
                            sumIncVat: entity.retailAmountWithVat,
                            adprice: entity.percentageOfMarkupRetail,
                        },
                        supplierCostInfo: {
                            vat: entity.supplierVatRatePercentage,
                            price: entity.supplierPriceExcludingVat,
                            vatPrice: entity.supplierVatPerUnit,
                            priceIncVat: entity.supplierPriceWithVat,
                            sum: entity.supplierAmountExcludingVat,
                            vatSum: entity.supplierVatAmount,
                            sumIncVat: entity.supplierAmountWithVat,
                            adprice: entity.supplierMarkupPercentage,
                        },
                        quantity: entity.quantity,
                        actualQuantity: entity.quantity
                            - (currentItem.damageQuantity ?? 0)
                            - (currentItem.defectQuantity ?? 0)
                            - (currentItem.shortageQuantity ?? 0),
                        registerPrice: entity.registeredPrice,
                        producerPrice: entity.manufacturerPrice,
                        productMargin: entity.actualMarkupPercentage,
                    };
                }),
            }));

            setDisplayItems(currentDisplayItems => currentDisplayItems.map(currentItem => {
                if (currentItem.idGlobal !== idGlobal) return currentItem;

                return {
                    ...currentItem,
                    retailVat: entity.percentageOfVatRateRetail,
                    retailPrice: entity.retailPrice,
                    retailPriceIncVat: entity.retailPriceWithVat,
                    retailAmount: entity.retailAmount,
                    retailAmountIncVat: entity.retailAmountWithVat,
                    supplierPrice: entity.supplierPriceExcludingVat,
                    supplierPriceIncVat: entity.supplierPriceWithVat,
                    supplierVat: entity.supplierVatRatePercentage,
                    supplierAmount: entity.supplierAmountExcludingVat, 
                    supplierAmountIncVat: entity.supplierAmountWithVat, 
                    quantity: entity.quantity,
                    actualQuantity: entity.quantity
                        - (currentItem.damageQuantity ?? 0)
                        - (currentItem.defectQuantity ?? 0)
                        - (currentItem.shortageQuantity ?? 0),
                    productMargin: entity.percentageOfMarkupRetail,
                    alerts: alertMessage
                };
            }));
        });
    }

    function changeItemScalingRatio(value: IInvoiceItemCreateDTO, scalingRatio?: IScalingRatioDTO) {
        if (!scalingRatio) return value;
        return {
            ...value,
            idScalingRatioGlobal: scalingRatio.idGlobal,
            numerator: scalingRatio.numerator,
            dateCreated: DateTime.now()
        } as IInvoiceItemCreateDTO;
    }

    const isValidDocument = (items: IInvoiceItemCreateDTO[], showMessage: () => void): boolean => {
        var flag = true;
        if (!items || items.length === 0) {
            flag = false;
            showMessage();
        }
        return flag;
    };

    const calcPercentageValue = (
        oldValue: number,
        type: PriceAdjustmentType,
        absolute: number,
        percent: number,
        adjustmentFactorType: AdjustmentFactorType,
    ) => {
        if (type === PriceAdjustmentType.MARKUP) {
            return adjustmentFactorType === AdjustmentFactorType.PERCENT
                ? oldValue + percent
                : oldValue * absolute;
        }

        return adjustmentFactorType === AdjustmentFactorType.PERCENT
            ? oldValue - percent
            : oldValue / absolute;
    };

    const calcAbsoluteValue = (
        oldValue: number,
        type: PriceAdjustmentType,
        absolute: number,
        percent: number,
        adjustmentFactorType: AdjustmentFactorType,
    ) => {
        if (type === PriceAdjustmentType.MARKUP) {
            return adjustmentFactorType === AdjustmentFactorType.PERCENT
                ? oldValue + oldValue * percent / 100
                : oldValue * absolute;
        }

        return adjustmentFactorType === AdjustmentFactorType.PERCENT
            ? oldValue - oldValue * percent / 100
            : oldValue / absolute;
    };

    const applyCalculatedInvoice = (calculatedInvoice: IPricingInvoiceViewDTO) => {
        const resultMap = new Map(calculatedInvoice.items.map(item => [item.idGlobal, item]));

        setData(prev => ({
            ...prev,
            items: prev.items.map(item => {
                const calculatedItem = resultMap.get(item.idGlobal);
                if (!calculatedItem) return item;

                return {
                    ...item,
                    retailCostInfo: {
                        vat: calculatedItem.percentageOfVatRateRetail,
                        price: calculatedItem.retailPrice,
                        vatPrice: calculatedItem.retailVatPerUnit,
                        priceIncVat: calculatedItem.retailPriceWithVat,
                        sum: calculatedItem.retailAmount,
                        vatSum: calculatedItem.vatAmountRetail,
                        sumIncVat: calculatedItem.retailAmountWithVat,
                        adprice: calculatedItem.percentageOfMarkupRetail,
                    },
                    supplierCostInfo: {
                        vat: calculatedItem.supplierVatRatePercentage,
                        price: calculatedItem.supplierPriceExcludingVat,
                        vatPrice: calculatedItem.supplierVatPerUnit,
                        priceIncVat: calculatedItem.supplierPriceWithVat,
                        sum: calculatedItem.supplierAmountExcludingVat,
                        vatSum: calculatedItem.supplierVatAmount,
                        sumIncVat: calculatedItem.supplierAmountWithVat,
                        adprice: calculatedItem.supplierMarkupPercentage,
                    },
                    quantity: calculatedItem.quantity,
                    actualQuantity: calculatedItem.quantity - (item.damageQuantity ?? 0) - (item.defectQuantity ?? 0) - (item.shortageQuantity ?? 0),
                    registerPrice: calculatedItem.registeredPrice,
                    producerPrice: calculatedItem.manufacturerPrice,
                    productMargin: calculatedItem.actualMarkupPercentage,
                };
            }),
        }));

        setDisplayItems(prev => prev.map(item => {
            const calculatedItem = resultMap.get(item.idGlobal);
            if (!calculatedItem) return item;

            const alerts = calculatedItem.retailPriceChangeReason
                ? [{
                    field: ["retailPriceIncVat", "productMargin"],
                    message: calculatedItem.retailPriceChangeReason,
                }]
                : [];

            return {
                ...item,
                retailVat: calculatedItem.percentageOfVatRateRetail,
                retailPrice: calculatedItem.retailPrice,
                retailPriceIncVat: calculatedItem.retailPriceWithVat,
                retailAmount: calculatedItem.retailAmount,
                retailAmountIncVat: calculatedItem.retailAmountWithVat,
                supplierPrice: calculatedItem.supplierPriceExcludingVat,
                supplierPriceIncVat: calculatedItem.supplierPriceWithVat,
                supplierVat: calculatedItem.supplierVatRatePercentage,
                supplierAmount: calculatedItem.supplierAmountExcludingVat,
                supplierAmountIncVat: calculatedItem.supplierAmountWithVat,
                quantity: calculatedItem.quantity,
                actualQuantity: calculatedItem.quantity - (item.damageQuantity ?? 0) - (item.defectQuantity ?? 0) - (item.shortageQuantity ?? 0),
                productMargin: calculatedItem.percentageOfMarkupRetail,
                alerts,
            };
        }));
    };

    const executeAllItemsCalculation = (
        eventType: eventPricingInvoiceTypes,
        getRetailCostInfo: (item: IInvoiceItemCreateDTO) => ICostInfoDTO,
        hasAdprice: boolean,
        hasRetailPrice: boolean,
    ) => {
        const pricingModelId = pricingModel?.idGlobal ?? data.idPricingModelGlobal;
        if (!pricingModelId || pricingModelId === uuidNull) {
            renderGlobalAlert({ variant: "error", statusCode: 500, title: baseT("messages.pricingModelNotSelected"), instance: baseT("pricingModelService") });
            return;
        }

        const itemsToCalculate = multipleSelect
            ? data.items.filter(item => positionIds.has(item.idGlobal))
            : data.items;
        if (itemsToCalculate.length === 0) return;

        const request: IPricingInvoiceDTO = {
            eventType,
            hasAdprice,
            hasRetailPrice,
            invoice: {
                idGlobal: data.idGlobal ?? uuidNull,
                idSupplierGlobal: data.idSupplierGlobal ?? uuidNull,
                idStoreGlobal: data.idStoreGlobal ?? uuidNull,
                items: itemsToCalculate.map(item => ({
                    idGlobal: item.idGlobal,
                    idGoodsGlobal: item.idGoodsGlobal ?? uuidNull,
                    idScalingRatioGlobal: item.idScalingRatioGlobal ?? uuidNull,
                    quantity: item.quantity,
                    producerPrice: item.producerPrice ?? 0,
                    supplierCostInfo: item.supplierCostInfo,
                    retailCostInfo: getRetailCostInfo(item),
                    registerPrice: item.registerPrice ?? 0,
                    actualQuantity: item.actualQuantity,
                    damageQuantity: item.damageQuantity,
                    defectQuantity: item.defectQuantity,
                    shortageQuantity: item.shortageQuantity,
                } as IInvoiceItem)),
            },
        };

        const chunkSize = 500;
        const calculateChunk = (startIndex: number) => {
            const chunkItems = request.invoice.items.slice(startIndex, startIndex + chunkSize);
            const chunkRequest: IPricingInvoiceDTO = {
                ...request,
                invoice: {
                    ...request.invoice,
                    items: chunkItems,
                },
            };

            pricingInvoiceDP.getCalculatedInvoiceViewAsync(pricingModelId, chunkRequest, calculatedInvoice => {
                applyCalculatedInvoice(calculatedInvoice);

                const nextIndex = startIndex + chunkSize;
                if (nextIndex < request.invoice.items.length) {
                    calculateChunk(nextIndex);
                    return;
                }

                setRecalcModal(<></>);
            });
        };

        calculateChunk(0);
    };

    function calcAllItems(groupConfirmed = false) {
        if (!data.items || data.items.length === 0 || !displayItems || displayItems.length === 0) return;
        if (multipleSelect && positionIds.size === 0) return;

        if (multipleSelect && !groupConfirmed) {
            setRecalcModal(
                <MessageModalWindow
                    message="Групповая обработка активна. Будут пересчитаны данные только для выделенных позиций."
                    ok={{ title: "ОК", onClick: () => calcAllItems(true) }}
                    cancel={{ onClick: () => setRecalcModal(<></>) }}
                />,
            );
            return;
        }

        setRecalcModal(
            <ActRevaluationRecalcModal
                header={`[${pricingModel?.displayName ?? ""}] Пересчитать розничную цену`}
                label=""
                options={[
                    { value: "recountModelValuesOption", label: t("documents.actRevaluation.recountModelValuesOption") },
                    { value: "recountFixRetailPrice", label: t("documents.actRevaluation.recountFixRetailPrice") },
                    { value: "recountVatUp", label: t("documents.actRevaluation.recountVatUp") },
                    { value: "recountWithMarkupPercent", label: t("documents.actRevaluation.recountWithMarkupPercent") },
                    { value: "recountWithMarkupOrMarkdown", label: t("documents.actRevaluation.recountWithMarkupOrMarkdown") },
                    { value: "recountToSupplierPriceIncVat", label: t("documents.actRevaluation.recountToSupplierPriceIncVat") },
                ]}
                onSelect={(value: string, markupPercent: number, markupOrMarkdownState: IMarkupOrMarkdownState) => {
                    switch (value) {
                        case "recountFixRetailPrice":
                            executeAllItemsCalculation("RetailPriceWithVatChanged", item => ({ ...item.retailCostInfo }), false, true);
                            break;
                        case "recountVatUp":
                            executeAllItemsCalculation("RetailPriceChanged", item => ({ ...item.retailCostInfo, price: item.retailCostInfo.priceIncVat }), false, true);
                            break;
                        case "recountWithMarkupPercent":
                            executeAllItemsCalculation("PercentageOfMarkupRetailChanged", item => ({ ...item.retailCostInfo, adprice: markupPercent }), true, false);
                            break;
                        case "recountWithMarkupOrMarkdown":
                            if (markupOrMarkdownState.priceAdjustmentMethod === PriceAdjustmentMethod.PERCENTAGE) {
                                executeAllItemsCalculation(
                                    "PercentageOfMarkupRetailChanged",
                                    item => ({
                                        ...item.retailCostInfo,
                                        adprice: calcPercentageValue(
                                            item.retailCostInfo.adprice,
                                            markupOrMarkdownState.priceAdjustmentType,
                                            markupOrMarkdownState.absolute,
                                            markupOrMarkdownState.percent,
                                            markupOrMarkdownState.adjustmentFactorType,
                                        ),
                                    }),
                                    true,
                                    false,
                                );
                            } else {
                                executeAllItemsCalculation(
                                    "RetailPriceWithVatChanged",
                                    item => ({
                                        ...item.retailCostInfo,
                                        priceIncVat: calcAbsoluteValue(
                                            item.retailCostInfo.priceIncVat,
                                            markupOrMarkdownState.priceAdjustmentType,
                                            markupOrMarkdownState.absolute,
                                            markupOrMarkdownState.percent,
                                            markupOrMarkdownState.adjustmentFactorType,
                                        ),
                                    }),
                                    true,
                                    false,
                                );
                            }
                            break;
                        case "recountToSupplierPriceIncVat":
                            executeAllItemsCalculation("RetailPriceWithVatChanged", item => ({ ...item.retailCostInfo, priceIncVat: item.supplierCostInfo.priceIncVat }), false, true);
                            break;
                        case "recountModelValuesOption":
                        default:
                            executeAllItemsCalculation("QuantityСhanged", item => ({ ...item.retailCostInfo }), false, false);
                            break;
                    }
                }}
                cancel={() => undefined}
                close={() => setRecalcModal(<></>)}
            />,
        );
    }

    function getItemsByGtin(barcode: string)
    {   
        itemsByGtin.setKizIdItemsByGtin(undefined)
        itemsByGtin.getItems(barcode, data.idGlobal as string)
    }

    function validateProducerPriceVsRegisterPrice(item: IInvoiceItemCreateDTO | undefined ) {
        const errors: IErrors[] = [];

        if (!item) {
            return errors;
        }
        const itemsArray = Array.isArray(item) ? item : [item];
        
        itemsArray.forEach((item) => {
            const producerPrice = item.producerPrice || 0;
            const registerPrice = item.registerPrice || 0;
            const isGnvls = item?.isGnvls;
            const itemFromDisplayItem = displayItems.find((displayItem) => displayItem.idGlobal === item.idGlobal);
            const itemName = itemFromDisplayItem?.goodsName
            
            if (registerPrice > 0 && producerPrice > registerPrice && isGnvls) {
                errors.push({
                    itemName: itemName,
                    producerPrice: producerPrice,
                    registerPrice: registerPrice,
                    message: `${t('documents.invoice.messages.producerPriceErrorPart1')} "${itemName}" ${t('documents.invoice.messages.producerPriceErrorPart2')} ${producerPrice} ${t('documents.invoice.messages.producerPriceErrorPart3')} ${registerPrice}`
                });
                errors.forEach(error => {
                    renderGlobalAlert({ variant: "warning", statusCode: 0, title: t('documents.invoice.messages.producerPriceTitle'), detail: error.message }); 
                });
            };
        });
        return errors;
    }

    const validateBeforeProcess = (row:IChangeDataGrid) => {
        const currentItem = data?.items.find((item) => item.idGlobal === row.idGlobal);
        const errors = validateProducerPriceVsRegisterPrice(currentItem);
        return errors.length === 0;
    };

    const calcRow =  (row:IChangeDataGrid) => { 
        const item = data.items.find((item) => item.idGlobal === row.idGlobal);
        if (!item) {
            return;
        }

        if (row.propertyName === "gtin") {
            setData(current => ({
                ...current,
                items: current.items.map(x => {
                    return {
                        ...x,
                        gtin: (x.idGlobal === row.idGlobal) ? row.value : (x.gtin as any)
                    };
                })
            }));
            setDisplayItems(current =>
                current.map((x): IDisplayItem => {
                    return {
                        ...x,
                        gtin: (x.idGlobal === row.idGlobal) ? row.value : (x.gtin as any)
                    };
                })
            );
        }

        if (row.propertyName === "quantity") {
            calcInvoiceItem(
                {
                    eventTypes: "QuantityСhanged",
                    idInvoice: "",
                    idSupplier: data.idSupplierGlobal,
                    idStore: data.idStoreGlobal,
                    idItem: "",
                    idGoods: item.idGoodsGlobal as string,
                    idScalingRatio: item.idScalingRatioGlobal,
                    quantity: row.value as number,
                    actualQuantity: item.actualQuantity,
                    damageQuantity: item.damageQuantity,
                    defectQuantity: item.defectQuantity,
                    shortageQuantity: item.shortageQuantity,
                    producerPrice: item.producerPrice,
                    retailCostInfo: { ...item.retailCostInfo, adprice: 0 as number },
                    supplierCostInfo: item.supplierCostInfo,
                    registerPrice: item.registerPrice,
                },
                row.idGlobal
            );
            return;
        }

        if (
            row.propertyName === "damageQuantity" ||
            row.propertyName === "defectQuantity" ||
            row.propertyName === "shortageQuantity"
        ) {
            const changedQuantity = Number(row.value);

            setData(prev => ({
                ...prev,
                items: prev.items.map(currentItem => {
                    if (currentItem.idGlobal !== row.idGlobal) return currentItem;

                    return { ...currentItem, [row.propertyName]: changedQuantity };
                }),
            }));

            return;
        }

        switch (row.propertyName) {
            case "retailAmount":
                calcRetailAmount(row.idGlobal, row.value as number, item);
                break;
            case "retailPriceIncVat":
                validateBeforeProcess(row)
                calcRetailPriceIncVat(row.idGlobal, row.value as number, item);
                break;
            case "productMargin":
                calcProductMargin(row.idGlobal, row.value as number, item, true);
                break;
        }
    }

    const changePositionGroup = (changes: IChangeDataGrid[]) => {
        const changesById = new Map(changes.map(change => [change.idGlobal, change]));
        setDisplayItems(current => current.map(item => {
            const change = changesById.get(item.idGlobal);
            return change ? { ...item, [change.propertyName]: change.value } : item;
        }));
        changes.forEach(change => calcRow(change));
    };

    const applyLatestPriceToSelectedItem = () => {
        if (!selectedItem || !selectedLatestPriceOption) {
            return;
        }

        const retailPriceIncVat = Number(selectedLatestPriceOption.displayName);
        if (!Number.isFinite(retailPriceIncVat)) {
            return;
        }

        calcRow({
            propertyName: "retailPriceIncVat",
            value: retailPriceIncVat,
            idGlobal: selectedItem.idGlobal,
        });
    };

    const handleSave = async (dataToSave: IInvoiceCreateDTO) => {
        try {
            const action = await loadSupplierDataCheckActionSettings();
            props.save(dataToSave, action, itemsWithErrors);
        } catch (error) {
            console.error('Ошибка при загрузке настроек:', error);
            props.save(dataToSave, supplierDataCheckAction || "NONE", itemsWithErrors);
        }
    };

    const handleOk = async (dataToProcess: IInvoiceCreateDTO) => {
        try {
            const action = await loadSupplierDataCheckActionSettings();
            props.ok(dataToProcess, action, itemsWithErrors);
        } catch (error) {
            console.error('Ошибка при загрузке настроек:', error);
            props.ok(dataToProcess, supplierDataCheckAction || "NONE", itemsWithErrors);
        }
    };

    const onOkAction = () => {
        lockingDocuments.delete({
            idTable: idTable,
            idDocument: data.idGlobal as string,
        });
		saveDocument(
            (dataToProcess) => handleOk(dataToProcess),
            () => controlKiz(data.items, () => setShowMessageModal({ 
                message: errorsT("kizCountNotMatch"), 
                show: true, 
                btSaveOrOk: "ok" 
            }), 
            documentType, 
            {
                requiredMode: false,
                validationType: sysOptionsKizValidationType
            })
        );
    };

    const onSaveAction = () => {
        saveDocument(
            (dataToSave) => handleSave(dataToSave),
            () => controlKiz(data.items, () => setShowMessageModal({
                message: errorsT("kizCountNotMatch"),
                show: true,
                btSaveOrOk: "save"
            }),
                documentType,
                {
                    requiredMode: false,
                    validationType: sysOptionsKizValidationType
                })
        );
    };

    const onCancel = () => {
        lockingDocuments.delete({
            idTable: idTable,
            idDocument: data.idGlobal as string,
        });

        props.cancel();
    };
	
    const pollAllTasks = async (taskIds: string[]): Promise<GtinInfoDto[]> => {
    const results: GtinInfoDto[] = [];
    const pendingTasks = new Set(taskIds);
    const maxAttempts = 60; // максимум 60 попыток (2 минуты)
    const attempts = new Map<string, number>();
    
    return new Promise((resolve) => {
        taskIds.forEach(taskId => {
            attempts.set(taskId, 0);
            
            const interval = setInterval(async () => {
                const currentAttempt = attempts.get(taskId) || 0;
                
                if (currentAttempt >= maxAttempts) {
                    clearInterval(interval);
                    pendingTasks.delete(taskId);
                    console.warn(`Превышено время ожидания для задачи ${taskId}`);
                    
                    if (pendingTasks.size === 0) {
                        resolve(results);
                    }
                    return;
                }
                
                attempts.set(taskId, currentAttempt + 1);
                
                try {
                    const response = await new Promise<GtinListInfoDto>((resolve, reject) => {
                        idp.getCheckSgtinsResult(taskId, data.idGlobal!,
                            (response) => resolve(response),
                            (error) => reject(error)
                        );
                    });
                    
                    // Если получили ответ (даже с ошибками) — завершаем задачу
                    if (response && response.gtinInfoList) {
                        clearInterval(interval);
                        pendingTasks.delete(taskId);
                        results.push(...response.gtinInfoList);
                        
                        if (pendingTasks.size === 0) {
                            resolve(results);
                        }
                    }
                } catch (error) {
                    const err = error as any;
                     if (err?.respType === "isFailed") {
                    clearInterval(interval);
                    pendingTasks.delete(taskId);
                    (results as any)._error = err?.message?.title || "Ошибка обработки запроса";
                    if (pendingTasks.size === 0) resolve(results);
                    return;
                    }
    
                    console.log(`Задача ${taskId} ещё не готова, попытка ${currentAttempt + 1}`);
                }
            }, 5000);
            
            setPollingIntervals(prev => [...prev, interval]);
        });
    });
    };


    useEffect(() => {
        return () => {
            pollingIntervals.forEach(interval => clearInterval(interval));
        };
    }, [pollingIntervals]);

const handleIgnoreCheck = async () => {
    console.log("1. handleIgnoreCheck started");
    
    if (!data.idGlobal) {
        console.log("2. no idGlobal");
        setShowResultsModal(false);
        return;
    }

    try {
        console.log("3. calling logIgnoreCheck");
        await new Promise<void>((resolve, reject) => {
            idp.logIgnoreCheck(
                data.idGlobal!,
                () => {
                    console.log("4. logIgnoreCheck success");
                    resolve();
                },
                (error) => {
                    console.log("5. logIgnoreCheck error", error);
                    reject(error);
                }
            );
        });
        console.log("6. after await");
    } catch (err) {
        console.error("7. catch error:", err);
    }

    console.log("8. closing modal and refreshing");
    setShowResultsModal(false);
    
    if (props.document?.idGlobal) {
        console.log("9. calling props.refresh");
        props.refresh?.();
    }
    console.log("10. done");
};

    const saveDocument = (callback: (data: IInvoiceCreateDTO) => void, controlKiz: () => boolean) => {
        if (
            isValid() &&
            isValidDocument(data.items, () => setShowMessageModalValidDocument({ message: errorsT("validDocument"), show: true })) === true &&
            controlKiz() === true
        ) {
            callback(data);
        }
    }

    const fillByGosContract = (shouldAddItems: boolean) => {
        const idGosContractGlobal = showMessageFillByGosContractGoods.entity?.idGlobal
        if (showMessageFillByGosContractGoods.entity?.idGlobal) {
            gosContractDP.getWithFullGoodsAsync(idGosContractGlobal as string, (entity) => {
                if (entity == null) {
                    setShowMessageFillByGosContractGoods({ show: false, message: "" })
                    return
                }

                setSupplier(entity.contractorLotWinner)
                setGosContract(showMessageFillByGosContractGoods.entity as IEntitySimpleDTO)
                setData({...data, idGosContractExternalGlobal: idGosContractGlobal, idSupplierGlobal: entity.contractorLotWinner.idGlobal})

                if (shouldAddItems) {
                    const invoiceItems: IInvoiceItemCreateDTO[] = [];
                    const displayItemsNew: IDisplayItem[] = [];

                    entity.goods.forEach(good => {
                        const idGlobal = uuidv4();
                        let quantity = good.qty;

                        if (good.executionAmount > 0) {
                            const executedQuantity = Math.floor(good.executionAmount / good.price);
                            quantity = quantity - executedQuantity
                        }

                        if (quantity <= 0) return
                        
                        const invoiceItem: IInvoiceItemCreateDTO = {
                            idGlobal: idGlobal,
                            goodsCode: good.goodsDto.code,
                            idGoodsGlobal: good.goodsDto.idGlobal as string,
                            isPieceAccountingKizOsu: false,
                            producer: good.goodsDto.producer.displayName,
                            producerPrice: 0,
                            productMargin: 0,
                            numerator: good.goodsDto.numerator,
                            denominator: good.goodsDto.denominator,
                            quantity,
                            retailCostInfo: { 
                                priceIncVat: good.price,
                                adprice: 0,
                                price: 0,
                                sum: 0,
                                vat: good.goodsDto.vatRetail,
                                sumIncVat: 0,
                                vatPrice: 0,
                                vatSum: 0,
                            } as ICostInfoDTO,
                            supplierCostInfo: {
                                priceIncVat: good.price,
                                adprice: 0,
                                price: 0,
                                sum: 0,
                                vat: good.goodsDto.vatRetail,
                                sumIncVat: 0,
                                vatPrice: 0,
                                vatSum: 0,
                            } as ICostInfoDTO,
                            actualQuantity: quantity,
                            idScalingRatioGlobal: good.goodsDto.scalingRatio.idGlobal,
                            idGosContractExternalGlobal: idGosContractGlobal,
                            idGosContractGoodsExternalGlobal: good.idGlobal,
                            registerPrice: good.goodsDto?.gnvls.registerPrice ?? 0,
                            dateCreated: DateTime.now(),
                        };

                        const displayItem: IDisplayItem = {
                            idGlobal: idGlobal,
                            idGoodsGlobal: good.goodsDto.idGlobal,
                            goodsName: good.goodsDto.goodsName.name,
                            scalingRatioName: good.goodsDto.scalingRatio.displayName,
                            quantity: quantity,
                            productMargin: 0,
                            producerPrice: 0,
                            registerPrice: 0,
                            retailPrice: 0,
                            retailPriceIncVat: good.price,
                            retailAmount: 0,
                            retailAmountIncVat: good.price * quantity,
                            retailVat: good.goodsDto.vatRetail,
                            supplierPrice: 0,
                            supplierAmount: 0,
                            supplierAmountIncVat: good.price * quantity,
                            supplierPriceIncVat: good.price,
                            supplierVat: good.goodsDto.vatRetail,
                            isKiz: false,
                            isGnvls: good.goodsDto.gnvls.important ?? false,
                            countKizScan: 0,
                            goodsCode: good.goodsDto.code,
                            producer: good.goodsDto.producer.displayName,
                            dateCreated: DateTime.now(),
                            seriesNumber: '',
                            bestBefore: DateTime.fromISO('1900-01-01'),
                            actualQuantity: quantity,
                            damageQuantity: 0,
                            defectQuantity: 0,
                            shortageQuantity: 0,
                            numerator: good.goodsDto.numerator,
                            denominator: good.goodsDto.denominator,
                            kizCategory: KIZ_CATEGORY.medicines
                        };

                        invoiceItems.push(invoiceItem);
                        displayItemsNew.push(displayItem);
                    });

                    setData(prev => {
                        const prevItems = Array.isArray(prev.items) ? prev.items : [];
                        return {
                            ...prev,
                            items: [...prevItems, ...invoiceItems]
                        };
                    });
                    setDisplayItems(prev => [...prev, ...displayItemsNew]);
                }
            })
        } 
    };

    const handleEntitySelect = (entity: any, searchQuery: string | undefined) => {
        if (searchOption.value === SearchOption.barcode || searchOption.value === SearchOption.goodsCode) {
            if (entity.length > 1) {
            setGoodsViewItem({ item: entity, show: true });
            } else {
            callOpenInvoiceItemCreatorPlugin(entity);
            }
        } else if (searchOption.value === SearchOption.gtin && searchQuery) {
            getItemsByGtin(searchQuery as string);
        } else {
            callOpenInvoiceItemCreatorPlugin(entity);
        }
    };

    useAutoSaveDocument(data.items, () => saveDocument(handleSave, () => {return true}))

    return (
        <>
            {/* Передаем ref для tour на кнопки "Сохранить" и "ОК". */}
            <BaseDocumentCreator
                actionButtonsRef={invoiceActionButtonsTourRef}
                valid={true}
                ok={{
                    onClick: onOkAction,
                    disabled: onlyRead || itemsValidation.some(x => x.err) || hasNegativeActualQuantity || props.isSubmitting,
                    sendRequestGlobal: props.isSubmitting,
                }}
                save={{
                    onClick: onSaveAction,
                    disabled: onlyRead || itemsValidation.some(x => x.err) || hasNegativeActualQuantity || props.isSubmitting,
                    sendRequestGlobal: props.isSubmitting,
                }}
                cancel={{ onClick: onCancel }}
                print={{
                    onClick: () => {
                        return idTable;
                    },
                    onSubItemClick: () => {
                        let arr = new Array<IKeyValuePair>();
                        if (props.document != undefined && props.document.mnemocode != undefined && props.idGlobal != undefined) arr.push({ key: props.idGlobal, value: props.document.mnemocode });
                        return arr;
                    },
                }}
                otherMenuActions={otherActions}
                checkKiz={props.variant === 'edit' && data.items && data.items.some(item => 
                (item.kizs && item.kizs.length > 0)) ? {
                title: baseT("checkKiz"),
                onClick: onCheckSgtinsAction,
            disabled: !data.items || data.items.length === 0 || onlyRead || props.isSubmitting,
            sendRequestSpinner: isCheckingKiz
            } : undefined}
            >
                <div className={styles.glwrapper} ref={formRef}>
                    <div className={styles.wrapper}>
                        <div>
                            {isCompact ? (
                                <>
                                    <GridWrapper cols={35}>
                                        <FlexRow className={styles.compactFieldFields} wrap>
                                            <div>
                                                <Button 
                                                    disabled={!data.items || data.items.length === 0 || onlyRead || disabledGosContract || (multipleSelect && positionIds.size === 0)}
                                                    className={styles.compactField_Recalculate}
                                                    variant="primary"
                                                    onClick={() => {
                                                        calcAllItems();
                                                    }} 
                                                >
                                                    Пересчитать
                                                </Button>
                                            </div>
                                            <GridSelectorInput
                                                required
                                                disabled={onlyRead || props.document?.isHardUnProcess}
                                                variant="required"
                                                selectorModalJsx={ContractorSelectorModal}
                                                id={"dictionaryInputInvoiceSupplier"}
                                                label={t("documents.invoice.supplier")}
                                                className={styles.compactFieldContractor}
                                                selectedEntity={supplier}
                                                onSelect={(value) => setSupplier({ ...value })}
                                                onClear={() => setSupplier(null)}
                                                error={errors.supplier}
                                                onFocus={() => setErrors({ ...errors, supplier: undefined })}
                                                treeViewCheckDirectoryType='supplier'
                                                applyUserFilter={true}
                                            />
                                            <TextInput
                                                label={t("documents.invoice.number")}
                                                inputId={"inputInvoiceNumber"}
                                                value={mnemocode}
                                                disabled
                                            />
                                            <DateInput
                                                required
                                                disabled={onlyRead}
                                                labelVariant="required"
                                                inputId={"inputInvoiceDate"}
                                                label={t("documents.invoice.date")}
                                                value={data.documentDate}
                                                error={errors.documentDate}
                                                onBlur={(value) => {
                                                    setData({ ...data, documentDate: value as DateTime });
                                                }}
                                            />
                                            <StoreSelectorPanel                                            
                                                we
                                                required
                                                compactMode
                                                idTable={idTable}
                                                contractor={{ 
                                                     label: t("documents.invoice.pharmacy"),
                                                     disabled: onlyRead || props.document?.isHardUnProcess,
                                                     treeViewCheckDirectoryType:'pharmacy'
                                                }}
                                                store={{
                                                    className: styles.gridSelInputCustom, 
                                                    label: t("documents.invoice.store"),
                                                    value: store,
                                                    onSelect: (value) => setStore(value),
                                                    onClear: () => setStore(null),
                                                    error: errors.store,
                                                    onFocus: () => setErrors({ ...errors, store: undefined }),
                                                    disabled: onlyRead || props.document?.isHardUnProcess
                                                }}
                                                pricingModel={{
                                                    label: t("documents.invoice.pricingModel"),
                                                    onSelect: (value) => setPricingModel(value),
                                                }}
                                                notAutoComplete={isCompactStore === true || props.variant === "edit" || props.variant === "copy"}
                                                documentStore={{ idGlobal: store?.idGlobal } as IEntitySimpleDTO}
                                                documentPricingModel={pricingModel ?? undefined}
                                            />
                                            <GridSelectorInput
                                                required
                                                disabled={onlyRead}
                                                variant="required"
                                                selectorModalJsx={PricingModelSelectorModal}
                                                selectedEntity={pricingModel}
                                                label={t("documents.invoice.pricingModel")}
                                                id={"dictionaryInputPricingModel"}
                                                onSelect={(entity) => setPricingModel({ ...entity })}
                                                onClear={() => setPricingModel(null)}
                                                error={errors.pricingModel}
                                                onFocus={() => {
                                                    setErrors({ ...errors, pricingModel: undefined });
                                                }}
                                            />
                                            <CheckBox
                                                id="isAgentContract"
                                                label={baseT("agentContract")}
                                                disabled={onlyRead}
                                                defaultChecked={data.isAgentContract ?? props.document?.isAgentContract ?? false}
                                                onChanged={(checked) => setData({ ...data, isAgentContract: checked })}
                                            />
                                            <CheckBox
                                                id="isGiftInvoice"
                                                label={baseT("isGiftInvoice")}
                                                disabled={onlyRead}
                                                defaultChecked={data.isGiftInvoice ?? props.document?.isGiftInvoice ?? false}
                                                onChanged={(checked) => setData({ ...data, isGiftInvoice: checked })}
                                            />
                                            <div className={styles.pricesSelectedBlock}>
                                                <div className={styles.field_PricesSelectedLine}>
                                                    <Select
                                                        label={t("documents.invoice.pricesSelectedLine")}
                                                        value={selectedLatestPriceOption}
                                                        onSelect={setSelectedLatestPriceOption}
                                                        options={latestPriceOptions}
                                                        defaultOption={latestPriceOptions[0]}
                                                    />
                                                </div>
                                                <div className={styles.field_recalculatePricesSelectedLineCompact} >
                                                    <Button
                                                        disabled={!selectedItem || !selectedLatestPriceOption || onlyRead}
                                                        variant="primary"
                                                        onClick={applyLatestPriceToSelectedItem}
                                                    >
                                                        {t("documents.invoice.recalculatePricesSelectedLine")}
                                                    </Button>
                                                </div>
                                            </div>
                                        </FlexRow>
                                        <div className={styles.compactFieldCompactMode}>
                                            <Tooltip title={baseT("basicModeUi")}>
                                                <StockBalanceButtonExpand
                                                    onClick={() => {
                                                        setIsCompact(!isCompact);
                                                    }}
                                                    sizeVariant="normal"
                                                    colorVariant="default"
                                                />
                                            </Tooltip>
                                        </div>
                                    </GridWrapper>
                                    <Accordion bold opened={false} caption="" id={uuidv4()}>
                                        <Accordion opened={false} caption={t("documents.invoice.addition")} id={`additionalInvoiceInfo-${uuidv4()}`}>
                                            <GridWrapper cols={2}>
                                                <GridSelectorInput
                                                    selectorModalJsx={GosContractSelectorModal}
                                                    selectedEntity={gosContract}
                                                    disabled={onlyRead || data.items?.some(item => !!item.idGosContractExternalGlobal)}
                                                    onSelect={(value: IEntitySimpleDTO) => {
                                                        setGosContract(value);
                                                        setData({ ...data, idGosContractExternalGlobal: value.idGlobal });
                                                    }}
                                                    label={t("documents.invoice.stateContract")}
                                                    onClear={() => {
                                                        setGosContract(null);
                                                        setData({ ...data, idGosContractExternalGlobal: undefined });
                                                    }}
                                                    inline={true}
                                                />
                                                <DictionaryInput 
                                                    disabled 
                                                    label={t("documents.invoice.contract")} 
                                                    inputId={"dictionaryInputContract"} 
                                                    inline={true} 
                                                />
                                            </GridWrapper>
                                        </Accordion>
                                        <Accordion opened={false} caption={t("documents.invoice.accompanyingDocuments")} id={`accompanyingInvoiceInfo-${uuidv4()}`}>
                                            <FlexRow wrap>
                                                <TextInput
                                                    disabled={onlyRead ? true : false}
                                                    label={t("documents.invoice.invoice")}
                                                    inputId={"inputAccompanyingInvoiceNumber"}
                                                    value={data?.supplierDocNumber}
                                                    onChange={(value) => setData({ ...data, supplierDocNumber: value })}
                                                />
                                                <DateInput
                                                    disabled={onlyRead ? true : false}
                                                    className={styles.field_InvoiceDate}
                                                    label={t("documents.invoice.date")}
                                                    inputId={"inputAccompanyingInvoiceDate"}
                                                    value={data?.supplierDocDate}
                                                    onBlur={(value) => value && setData({ ...data, supplierDocDate: value })}
                                                />
                                                <DateInput
                                                    disabled={onlyRead ? true : false}
                                                    className={styles.field_InvoicePaymentDate}
                                                    label={t("documents.invoice.dateOfPayment")}
                                                    inputId={"inputAccompanyingInvoicePaymentDate"}
                                                    value={data?.datePay}
                                                    onBlur={(value) => value && setData({ ...data, datePay: value })}
                                                />
                                                <DateInput
                                                    className={styles.field_InvoiceActualPaymentDate}
                                                    disabled
                                                    label={t("documents.invoice.inputAccompanyingInvoiceActualPaymentDate")}
                                                    inputId={"inputAccompanyingInvoiceActualPaymentDate"}
                                                />
                                                <TextInput
                                                    disabled={onlyRead ? true : false}
                                                    label={t("documents.invoice.check")}
                                                    inputId={"inputAccompanyingBillNumber"}
                                                    value={data?.supplierBillDocNumber}
                                                    onChange={(value) => {
                                                        setData({ ...data, supplierBillDocNumber: value });
                                                    }}
                                                />
                                                <DateInput
                                                    disabled={onlyRead ? true : false}
                                                    className={styles.field_BillDate}
                                                    label={t("documents.invoice.date")}
                                                    inputId={"inputAccompanyingBillDate"}
                                                    value={data?.supplierBillDocDate}
                                                    onBlur={(value) => {
                                                        value && setData({ ...data, supplierBillDocDate: value });
                                                    }}
                                                />
                                                <TextInput
                                                    className={styles.field_TargetProgram}
                                                    disabled
                                                    label={t("documents.invoice.targetProgram")}
                                                    inputId={"inputAccompanyingTargetProgram"}
                                                    /*value={accompanyingInvoiceInfo?.targetProgram} onChange={(value) => setAccompanyingInvoiceInfo({ ...accompanyingInvoiceInfo, targetProgram: value })}*/
                                                />
                                            </FlexRow>
                                        </Accordion>
                                    </Accordion>
                                </>
                            ) : (
                                <>
                                    {/* Target для tour: основные поля приходной накладной. */}
                                    <div ref={invoiceMainFieldsTourRef}>
                                    <GridWrapper cols={4} baseline>
                                        <GridSelectorInput
                                            required
                                            disabled={onlyRead|| props.document?.isHardUnProcess}
                                            variant="required"
                                            selectorModalJsx={ContractorSelectorModal}
                                            className={styles.field_Provider}
                                            inline
                                            id={"dictionaryInputInvoiceSupplier"}
                                            label={t("documents.invoice.supplier")}
                                            selectedEntity={supplier}
                                            onSelect={(value) => setSupplier({ ...value })}
                                            onClear={() => setSupplier(null)}
                                            error={errors.supplier}
                                            onFocus={() => setErrors({ ...errors, supplier: undefined })}
                                            treeViewCheckDirectoryType="supplier"
                                            applyUserFilter={true}
                                        />
                                        <TextInput
                                            className={styles.field_Number}
                                            disabled
                                            label={t("documents.invoice.number")}
                                            inputId={"inputInvoiceNumber"}
                                            inline={true}
                                            value={mnemocode}
                                        />
                                        <DateInput
                                            required
                                            disabled={onlyRead}
                                            labelVariant="required"
                                            inputId={"inputInvoiceDate"}
                                            className={styles.field_Date}
                                            label={t("documents.invoice.date")}
                                            inline
                                            value={data.documentDate}
                                            error={errors.documentDate}
                                            onBlur={(value) => {
                                                setData({ ...data, documentDate: value as DateTime });
                                            }}
                                        />
                                        <div className={styles.field_CompactMode}>
                                            <Tooltip title={baseT("compactModeUi")}>
                                                <StockBalanceButtonCompress
                                                    onClick={() => {
                                                        setIsCompact(!isCompact);
                                                        setIsCompactStore(true);
                                                    }}
                                                    sizeVariant="normal"
                                                    colorVariant="default"
                                                />
                                            </Tooltip>
                                        </div>
                                    </GridWrapper>
                                    <div>
                                        <GridWrapper cols={4} baseline>
                                            <StoreSelectorPanel
                                                we
                                                required
                                                idTable={idTable}
                                                contractor={{ 
                                                    label: t("documents.invoice.pharmacy"),
                                                    disabled: onlyRead ? true : false || props.document?.isHardUnProcess,
                                                    treeViewCheckDirectoryType:'pharmacy'
                                                }}
                                                store={{
                                                    className: styles.gridSelInputCustom, 
                                                    label: t("documents.invoice.store"),
                                                    value: store,
                                                    onSelect: (value) => setStore(value),
                                                    onClear: () => setStore(null),
                                                    error: errors.store,
                                                    onFocus: () => setErrors({ ...errors, store: undefined }),
                                                    disabled: onlyRead ? true : false || props.document?.isHardUnProcess
                                                }}
                                                pricingModel={{
                                                    label: t("documents.invoice.pricingModel"),
                                                    onSelect: (value) => setPricingModel(value),
                                                }}
                                                notAutoComplete={isCompactStore === true || props.variant === "edit" || props.variant === "copy"}
                                                documentStore={{ idGlobal: store?.idGlobal } as IEntitySimpleDTO}
                                                documentPricingModel={pricingModel ?? undefined}
                                            />
                                            <CheckBox
                                                id="isAgentContract"
                                                label="Агентский контракт"
                                                disabled={onlyRead}
                                                defaultChecked={data.isAgentContract ?? props.document?.isAgentContract ?? false}
                                                onChanged={(checked) => setData({ ...data, isAgentContract: checked })}
                                            />
                                            <CheckBox
                                                id="isGiftInvoice"
                                                label={baseT("isGiftInvoice")}
                                                disabled={onlyRead}
                                                defaultChecked={data.isGiftInvoice ?? props.document?.isGiftInvoice ?? false}
                                                onChanged={(checked) => setData({ ...data, isGiftInvoice: checked })}
                                            />
                                        </GridWrapper>
                                    </div></div>
                                    <GridWrapper cols={6}>
                                        {/* Target для tour: выбор модели ценообразования. */}
                                        <div ref={invoicePricingModelTourRef} className={styles.field_PriceModel}>
                                            <GridSelectorInput
                                                required
                                                variant="required"
                                                disabled={onlyRead}
                                                selectorModalJsx={PricingModelSelectorModal}
                                                label={t("documents.invoice.pricingModel")}
                                                id={"dictionaryInputPricingModel"}
                                                inline
                                                selectedEntity={pricingModel}
                                                onSelect={(entity) => setPricingModel({ ...entity })}
                                                error={errors.pricingModel}
                                                onClear={() => setPricingModel(null)}
                                                onFocus={() => {
                                                    setErrors({ ...errors, pricingModel: undefined });
                                                }}
                                            />
                                        </div>
                                        <div className={styles.field_Recalculate}>
                                            <Button
                                                disabled={!data.items || data.items.length === 0 || onlyRead || disabledGosContract || (multipleSelect && positionIds.size === 0)}
                                                variant="primary"
                                                onClick={() => {
                                                    calcAllItems();
                                                }}
                                            >
                                                {t("documents.invoice.recalculate")}
                                            </Button>
                                        </div>
                                        <div className={styles.field_PricesSelectedLine}>
                                            <Select
                                                label={t("documents.invoice.pricesSelectedLine")}
                                                inline
                                                value={selectedLatestPriceOption}
                                                onSelect={setSelectedLatestPriceOption}
                                                options={latestPriceOptions}
                                                defaultOption={latestPriceOptions[0]}
                                            />
                                        </div>
                                        <div className={styles.field_RecalculatePricesSelectedLine} >
                                            <Button
                                                disabled={!selectedItem || !selectedLatestPriceOption || onlyRead}
                                                variant="primary"
                                                onClick={applyLatestPriceToSelectedItem}
                                            >
                                                {t("documents.invoice.recalculatePricesSelectedLine")}
                                            </Button>
                                        </div>
                                    </GridWrapper>

                                    <Accordion opened={false} caption={t("documents.invoice.addition")} id={`additionalInvoiceInfo-${uuidv4()}`}>
                                        <GridWrapper cols={2}>
                                            <GridSelectorInput
                                                selectorModalJsx={GosContractSearchSelectorModal}
                                                selectedEntity={gosContract}
                                                disabled={onlyRead || data.items?.some(item => !!item.idGosContractExternalGlobal)}
                                                onSelect={(value: IEntitySimpleDTO) => {
                                                    const requestVersion = ++gosContractSupplierRequestVersion.current;
                                                    setGosContract(value);
                                                    setData(previous => ({ ...previous, idGosContractExternalGlobal: value.idGlobal }));
                                                    if (useLotWinnerAsInvoiceSupplier) {
                                                        gosContractDP.getById(value.idGlobal, (entity) => {
                                                            if (requestVersion !== gosContractSupplierRequestVersion.current) return;
                                                            setSupplier(entity?.contractorLotWinner ?? null);
                                                        });
                                                    }
                                                }}
                                                label={t("documents.invoice.stateContract")}
                                                onClear={() => {
                                                    gosContractSupplierRequestVersion.current++;
                                                    setGosContract(null);
                                                    setData(previous => ({ ...previous, idGosContractExternalGlobal: undefined }));
                                                }}
                                                inline
                                            />
                                            <DictionaryInput disabled label={t("documents.invoice.contract")} inputId={"dictionaryInputContract"} inline={true} />
                                        </GridWrapper>
                                    </Accordion>
                                    {/* Target для tour: сопроводительные документы. */}
                                    <div ref={invoiceAccompanyingDocumentsTourRef}>
                                        <Accordion opened={false} caption={t("documents.invoice.accompanyingDocuments")} id={`accompanyingInvoiceInfo-${uuidv4()}`}>
                                            <GridWrapper cols={4}>
                                                <TextInput
                                                    disabled={onlyRead}
                                                    className={styles.field_InvoiceNumber}
                                                    labelClassName={styles.invoiceNumberLabel}
                                                    label={t("documents.invoice.invoice")}
                                                    inputId={"inputAccompanyingInvoiceNumber"}
                                                    inline
                                                    value={data?.supplierDocNumber}
                                                    onChange={(value) => setData({ ...data, supplierDocNumber: value })}
                                                />
                                                <DateInput
                                                    disabled={onlyRead}
                                                    className={styles.field_InvoiceDate}
                                                    label={t("documents.invoice.date")}
                                                    inputId={"inputAccompanyingInvoiceDate"}
                                                    inline
                                                    value={data?.supplierDocDate}
                                                    onBlur={(value) => value && setData({ ...data, supplierDocDate: value })}
                                                />
                                                <DateInput
                                                    disabled={onlyRead}
                                                    className={styles.field_InvoicePaymentDate}
                                                    label={t("documents.invoice.dateOfPayment")}
                                                    inputId={"inputAccompanyingInvoicePaymentDate"}
                                                    inline
                                                    value={data?.datePay}
                                                    onBlur={(value) => value && setData({ ...data, datePay: value })}
                                                />
                                                <DateInput
                                                    className={styles.field_InvoiceActualPaymentDate}
                                                    disabled
                                                    label={t("documents.invoice.inputAccompanyingInvoiceActualPaymentDate")}
                                                    inputId={"inputAccompanyingInvoiceActualPaymentDate"}
                                                    inline
                                                />
                                            </GridWrapper>

                                            <GridWrapper cols={4}>
                                                <TextInput
                                                    disabled={onlyRead}
                                                    className={styles.field_BillNumber}
                                                    label={t("documents.invoice.check")}
                                                    inputId={"inputAccompanyingBillNumber"}
                                                    inline
                                                    value={data?.supplierBillDocNumber}
                                                    onChange={(value) => {
                                                        setData({ ...data, supplierBillDocNumber: value });
                                                    }}
                                                />
                                                <DateInput
                                                    disabled={onlyRead}
                                                    className={styles.field_BillDate}
                                                    label={t("documents.invoice.date")}
                                                    inputId={"inputAccompanyingBillDate"}
                                                    inline
                                                    value={data?.supplierBillDocDate}
                                                    onBlur={(value) => {
                                                        value && setData({ ...data, supplierBillDocDate: value });
                                                    }}
                                                />

                                                <TextInput
                                                    className={styles.field_TargetProgram}
                                                    disabled
                                                    label={t("documents.invoice.targetProgram")}
                                                    inputId={"inputAccompanyingTargetProgram"}
                                                    inline
                                                />
                                            </GridWrapper>
                                        </Accordion>
                                    </div>
                                </>
                            )}
                            <FlexRow className={styles.field_flexbox}>
                                <PositionEditPanel
                                    groupProcessing={{
                                        value: multipleSelect,
                                        onClick: () => {
                                            setMultipleSelect(value => !value);
                                            setSelectedItems([]);
                                            setSelectedItem(undefined);
                                            selectedItemRef.current = undefined;
                                        },
                                    }}
                                    /* Target для tour внутри PositionEditPanel. */
                                    tourRefs={{
                                        kiz: invoicePositionEditPanelTourRef,
                                    }}
                                    kiz={{
                                        onClick: () => {
                                            itemsByGtin.setKizIdItemsByGtin(undefined)
                                            setShowKizScan(true)
                                            setKizScanComponent(selectedItemRef.current)
                                        },
                                        disabled:
                                            (((selectedItemCreateDto?.isKiz || selectedItemCreateDto?.isKizBox) && !onlyRead)
                                                ? false
                                                : true) || props.document?.isHardUnProcess,
                                    }}
                                    edit={{
                                        onClick: async () => {
                                            if (!selectedItem) return;

                                            // Получаем актуальный элемент из данных
                                            const itemDto = data.items.find((x) => x.idGlobal === selectedItem.idGlobal);
                                            if (!itemDto) return;

                                            try {
                                                // Запрашиваем scalingRatio напрямую
                                                const scalingRatio = await new Promise<IScalingRatioDTO | null>((resolve) => {
                                                    scalingRatioDP.overrideGetById(
                                                        itemDto.idGoodsGlobal,
                                                        itemDto.idScalingRatioGlobal as string,
                                                        (result) => resolve(result)
                                                    );
                                                });

                                                // Запрашиваем goods напрямую
                                                const goods = await new Promise<IGoodsDTO | null>((resolve) => {
                                                    goodsDP.getById(itemDto.idGoodsGlobal, (result) => resolve(result));
                                                });

                                                // Проверяем, что данные получены
                                                if (!scalingRatio || !goods) {
                                                    console.error("Не удалось загрузить данные для редактирования");
                                                    return;
                                                }

                                                // Открываем модальное окно с актуальными данными
                                                if (data.idGosContractExternalGlobal) {
                                                    gosContractDP.getById(data.idGosContractExternalGlobal, (gosContract: IGosContractDTO) => {
                                                        const gosContractGoods = gosContract.goods.find(
                                                            (x) => x.goods.idGlobal === goods.idGlobal
                                                        );

                                                        tabsContext.openChild(
                                                            "invoice_item_creator_plugin",
                                                            "edit",
                                                            undefined,
                                                            (value: IInvoiceItemCreateDTO, displayItem: IDisplayItem) => {
                                                                setData({
                                                                    ...data,
                                                                    items: [...data.items.filter((x) => x.idGlobal !== value.idGlobal), value],
                                                                });
                                                                setDisplayItems([...displayItems.filter((x) => x.idGlobal !== displayItem.idGlobal), displayItem]);
                                                            },
                                                            {
                                                                item: itemDto,
                                                                goods: {
                                                                    displayName: goods.goodsName.name as string,
                                                                    idGlobal: goods.idGlobal as string,
                                                                } as IEntitySimpleDTO,
                                                                displayItem: displayItems.find((x) => x.idGlobal === selectedItem.idGlobal),
                                                                idPricingModel: data.idPricingModelGlobal,
                                                                idSupplier: data.idSupplierGlobal,
                                                                idStore: data.idStoreGlobal,
                                                                scalingRatio: {
                                                                    displayName: scalingRatio.displayName,
                                                                    idGlobal: scalingRatio.idGlobal
                                                                } as IEntitySimpleDTO,
                                                                gosContractFields: {
                                                                    isGosContract: true,
                                                                    gosContractGoods: gosContractGoods ?? null,
                                                                    idGosContractGlobal: data.idGosContractExternalGlobal
                                                                } as IGosContractFields,
                                                                currentSupplier: currentSupplier,
                                                                currentPharmacy: currentPharmacy,
                                                                variant: 'edit',
                                                                onlyRead: onlyRead,
                                                                isUnProcessHard: props.document?.isHardUnProcess,
                                                                documentDate: data.documentDate,
                                                                invoiceItemsGroup: data.items.filter((element) =>
                                                                    element.idInvoiceItemGroupGlobal === itemDto.idInvoiceItemGroupGlobal
                                                                ),
                                                                isGiftInvoice: data.isGiftInvoice
                                                            }
                                                        );
                                                    });
                                                } else {
                                                    tabsContext.openChild(
                                                        "invoice_item_creator_plugin",
                                                        "edit",
                                                        undefined,
                                                        (value: IInvoiceItemCreateDTO, displayItem: IDisplayItem) => {
                                                            setData({
                                                                ...data,
                                                                items: [...data.items.filter((x) => x.idGlobal !== value.idGlobal), value],
                                                            });
                                                            setDisplayItems([...displayItems.filter((x) => x.idGlobal !== displayItem.idGlobal), displayItem]);
                                                        },
                                                        {
                                                            item: itemDto,
                                                            goods: {
                                                                displayName: goods.goodsName.name as string,
                                                                idGlobal: goods.idGlobal as string,
                                                            } as IEntitySimpleDTO,
                                                            scalingRatio: {
                                                                displayName: scalingRatio.displayName,
                                                                idGlobal: scalingRatio.idGlobal
                                                            } as IEntitySimpleDTO,
                                                            displayItem: displayItems.find((x) => x.idGlobal === selectedItem.idGlobal),
                                                            idPricingModel: data.idPricingModelGlobal,
                                                            idSupplier: data.idSupplierGlobal,
                                                            idStore: data.idStoreGlobal,
                                                            gosContractFields: {
                                                                isGosContract: false,
                                                                gosContractGoods: null,
                                                            },
                                                            currentSupplier: currentSupplier,
                                                            currentPharmacy: currentPharmacy,
                                                            variant: 'edit',
                                                            isUnProcessHard: props.document?.isHardUnProcess,
                                                            onlyRead: onlyRead,
                                                            documentDate: data.documentDate,
                                                            invoiceItemsGroup: data.items.filter((element) =>
                                                                element.idInvoiceItemGroupGlobal === itemDto.idInvoiceItemGroupGlobal
                                                            ),
                                                            isGiftInvoice: data.isGiftInvoice
                                                        }
                                                    );
                                                }
                                            } catch (error) {
                                                console.error("Ошибка при загрузке данных для редактирования:", error);
                                            }
                                        },
                                        disabled: multipleSelect || !selectedItem,
                                    }}
                                    delete={{
                                        onClick: () => {
                                            if (positionIds.size === 0 || isCopyingPositions) return;

                                            // Удаляем элемент из основных данных
                                            setData({
                                                ...data,
                                                items: data.items.filter((x) => !positionIds.has(x.idGlobal)),
                                            });

                                            // Удаляем из отображаемых элементов
                                            setDisplayItems(displayItems.filter((x) => !positionIds.has(x.idGlobal)));

                                            // Очищаем выбор
                                            setSelectedItems([]);
                                            setSelectedItem(undefined);
                                            selectedItemRef.current = undefined

                                            // Удаляем из валидаций
                                            const itemsValid = itemsValidation.filter((item) => !positionIds.has(item.idGlobal));
                                            const itemsFilterValid = itemsFilter.filter((item) => !positionIds.has(item.idItem));
                                            setItemsValidation(itemsValid);
                                            setItemsFilter(itemsFilterValid);
                                        },
                                        disabled: positionIds.size === 0 || isCopyingPositions || onlyRead || props.document?.isHardUnProcess
                                    }}
                                    copy={{
                                        onClick: async () => {
                                            if (positionIds.size === 0 || isCopyingPositions) return;
                                            setIsCopyingPositions(true);
                                            try {
                                                for (const itemId of positionIds) {
                                                    const itemToCopy = data.items.find((x) => x.idGlobal === itemId);
                                                    const displayItemToCopy = displayItems.find((x) => x.idGlobal === itemId)
                                                    const idGlobalItemCopy = uuidv4()
                                                    let idGlobalSeriesCopy: string | undefined

                                                    if (!itemToCopy) continue;

                                                    if (itemToCopy.idSeriesGlobal) {
                                                        try {
                                                            idGlobalSeriesCopy = uuidv4()
                                                            const response = await new Promise<ISerialDTO>((resolve, reject) => {
                                                                sdp.getById(itemToCopy.idSeriesGlobal as string, (response) => {
                                                                    resolve(response);
                                                                });
                                                            });

                                                            const seriesCopy = {...response, idGlobal: idGlobalSeriesCopy}
                                                            idGlobalSeriesCopy = await new Promise((resolve) => {
                                                                sdp.create(seriesCopy, (idGlobal) => {
                                                                    resolve(idGlobal);
                                                                });
                                                            });
                                                        } catch (error) {
                                                            console.error('Error copying series:', error);
                                                        }
                                                    }

                                                    const copiedItem: IInvoiceItemCreateDTO = {
                                                        ...itemToCopy,
                                                        idGlobal: idGlobalItemCopy,
                                                        idSeriesGlobal: idGlobalSeriesCopy,
                                                        kizCount: 0,
                                                        kizBoxCount: 0,
                                                        kizs: [],
                                                        kizOsuDtos: [],
                                                        kizBoxes: [],
                                                        kizBoxesGisMt: []
                                                    };

                                                    setData(current => ({
                                                        ...current,
                                                        items: [...current.items, copiedItem]
                                                    }));

                                                    if (!displayItemToCopy) continue;

                                                    const copiedDisplayItem: IDisplayItem = {
                                                        ...displayItemToCopy,
                                                        idGlobal: idGlobalItemCopy,
                                                        countKizScan: 0
                                                    };

                                                    setDisplayItems(current => [...current, copiedDisplayItem]);

                                                    const itemValidationsToCopy = itemsValidation.filter((item) => item.idGlobal === itemId); //

                                                    if (itemValidationsToCopy.length > 0) {
                                                        const copiedValidations = itemValidationsToCopy.map(validation => ({
                                                            ...validation,
                                                            idGlobal: idGlobalItemCopy
                                                        }));

                                                        setItemsValidation(current => [...current, ...copiedValidations]);
                                                    }

                                                    const itemFiltersToCopy = itemsFilter.filter((item) => item.idItem === itemId);

                                                    if (itemFiltersToCopy.length > 0) {
                                                        const copiedFilters = itemFiltersToCopy.map(filter => ({
                                                            ...filter,
                                                            idItem: idGlobalItemCopy
                                                        }));

                                                        setItemsFilter(current => [...current, ...copiedFilters]);
                                                    }
                                                }
                                            } finally {
                                                setIsCopyingPositions(false);
                                            }
                                        },
                                        disabled: positionIds.size === 0 || isCopyingPositions || onlyRead || props.document?.isHardUnProcess
                                    }}
                                />
                                {/* Target для tour: подбор товара. */}
                                <div ref={invoiceGoodsSelectionTourRef} className={styles.field_SelectionGoods}>
                                    <GridSelectorInput
                                        barcodeScan={tabsContext.currentTab && tabsContext.currentTab?.id === currentIdTab.current}
                                        disabled={onlyRead ? true : false || props.document?.isHardUnProcess}
                                        selectorModalJsx={GoodsSelectorModal}
                                        gosContract={data?.idGosContractExternalGlobal}
                                        searchOption={searchOption.value}
                                        error={errorSelectGoods}
                                        onKeyDown={(e) => {
                                            checkCapsLock(e, (isCapsLock)=> {
                                                 if (isCapsLock) {
                                                    setErrorSelectGoods(t("documents.invoice.capsLockOn"));
                                                    return
                                                }
                                                else {
                                                    setErrorSelectGoods(undefined);
                                                }
                                            })
                                        }}
                                        id={"inputAccompanyingSelectionGoods"}
                                        inline={true}
                                        label={t("documents.invoice.selectionOfGoods")}
                                        valid={() => isValid()}
                                        onSelect={(entity,searchQuery) => {
                                            handleEntitySelect(entity,searchQuery)
                                        }}
                                        lastSearchQuerySettings = {lastSearchQuerySettings}
                                        autoFocus={showLastSearchQuery}
                                    />
                                </div>
                                {/* Target для tour: фильтр режима поиска. */}
                                <div ref={invoiceSearchOptionTourRef} style={{ display: "flex" }}>
                                    <Select
                                        disabled={onlyRead}
                                        barcodeScan={tabsContext.currentTab && tabsContext.currentTab?.id === currentIdTab.current}
                                        value={searchOption}
                                        onSelect={(option) => {
                                            setSearchOption(option);
                                        }}
                                        className={styles.field_SelectBlock}
                                        options={[
                                            { displayName: t("documents.invoice.barcode"), value: "barcode" },
                                            { displayName: t("documents.invoice.name"), value: "name" },
                                            { displayName: t("documents.invoice.goodsCode"), value: "goodsCode" },
                                            { displayName: t("documents.invoice.gtin"), value: SearchOption.gtin, hidden: props.variant !== "edit"} 
                                        ]}
                                        defaultOption={defaultSearchOption}
                                    />
                                </div>
                                
                                <div className={styles.SettingsButton}>
                                    <TabIndexSettingsButtonModal 
                                        showModal={showSettings}
                                        modalWindow={SettingsModal}
                                    />
                                </div>
                            </FlexRow>
                            {/* Target для tour: таблица позиций накладной. */}
                            <div ref={invoiceItemsGridTourRef}>
                                <ChangeableGrid
                                    virtualizedData
                                    visibleRows={28}
                                    documentStateProc={onlyRead}
                                    itemsFilter={itemsFilter}
                                    itemsValidation={itemsValidation}
                                    setItemsValidation={(value) => {
                                        const newItemsValidation = itemsValidation.map((item) => (item.idGlobal === value[0].idGlobal ? { ...item, ...value } : item));
                                        setItemsValidation(newItemsValidation);
                                    }}
                                    gridId={uuidv4()}
                                    items={data?.items}
                                    data={displayItems}
                                    documentType={documentType}
                                    sysOptionsKizValidationType={sysOptionsKizValidationType}
                                    autoSelect
                                    selectById={duplicateKizItemId || invoiceItemWithErrorId}
                                    hiddenPagination={undefined}
                                    filter={gridFilter}
                                    totalCount={displayItems.length}
                                    plugin={InvoiceCreatorItemGridPluginSettings()}
                                    selectedItem={selectedItem}
                                    multipleSelect={multipleSelect}
                                    selectedItems={selectedItems}
                                    onMultipleSelect={setSelectedItems}
                                    groupEditing={{
                                        excludedColumns: ["quantity", "damageQuantity", "defectQuantity", "shortageQuantity", "gtin"],
                                        onChange: changePositionGroup,
                                    }}
                                    onColumnEnter={(row: IChangeDataGrid) => {
                                        if (!multipleSelect) clearTimer(() => calcRow(row));
                                    }}
                                    onChangeData={(row: IChangeDataGrid) => {
                                        if (multipleSelect) {
                                            changePositionGroup([row]);
                                            return;
                                        }
                                        if (row.propertyName === "quantity") {
                                            const quantity = Number(row.value);

                                            setData(currentData => ({
                                                ...currentData,
                                                items: currentData.items.map(currentItem => {
                                                    if (currentItem.idGlobal !== row.idGlobal) return currentItem;

                                                    return {
                                                        ...currentItem,
                                                        quantity,
                                                        actualQuantity: quantity
                                                            - (currentItem.damageQuantity ?? 0)
                                                            - (currentItem.defectQuantity ?? 0)
                                                            - (currentItem.shortageQuantity ?? 0),
                                                    };
                                                }),
                                            }));

                                            setTimer(() => calcRow(row));
                                            return;
                                        }

                                        if (
                                            row.propertyName === "damageQuantity" ||
                                            row.propertyName === "defectQuantity" ||
                                            row.propertyName === "shortageQuantity"
                                        ) {
                                            clearTimer(() => calcRow(row));
                                            return;
                                        }

                                        setTimer(() => calcRow(row));
                                    }}
                                    enableSortByAlerts
                                    onSelect={(row) => {
                                        setSelectedItem(row)
                                        selectedItemRef.current = row
                                    }}
                                    onDoubleClick={(row) => props.onDoubleClick?.(row)}
                                    onSort={(i) => dispatchGridFilter({ type: "sort", payload: i.propertyName })}
                                    onFilterDelete={(i) => dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName })}
                                    onPageNumberChange={(n) => dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } })}
                                    onNumberPerPageChange={(n) => dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })}
                                    searching
                                    mapDisabledField={disabledGosContract ? gosContractMap : undefined}
                                    isCellInvalid={(cell) => {
                                        // При отрицательном фактическом количестве красим только поля
                                        // «Бой», «Брак» и «Недостача».
                                        if (
                                            cell.propertyName !== "damageQuantity" &&
                                            cell.propertyName !== "defectQuantity" &&
                                            cell.propertyName !== "shortageQuantity"
                                        ) {
                                            return false;
                                        }

                                        const item = data.items.find(currentItem => currentItem.idGlobal === cell.idGlobal);
                                        if (!item) return false;

                                        // Количество факт. = Количество - Бой - Брак - Недостача.
                                        return item.quantity
                                            - (item.damageQuantity ?? 0)
                                            - (item.defectQuantity ?? 0)
                                            - (item.shortageQuantity ?? 0) < 0;
                                    }}
                                    getStyleRow={(item) => {
                                        const cell = item.cells.find(x => x.propertyName === 'isGnvls');
                                        return ((cell?.value === "Да" || cell?.value === true ) && coloredRowIsGnvls) ? stylesIndex.internetOrderNumberCells : null;
                                    }}

                                    renderCustomCell={(cell: IGridCellWithWidth, row: IGridRow) => {

                                        const currentDisplayItem = displayItems.find(
                                            item => item.idGlobal === row.idGlobal
                                        );

    
                                        if (cell.propertyName === "lineStatus") {
                                            return <InvoiceStatusCell status={cell.value as string} />;
                                        };
    
                                        if (cell.propertyName === "alerts" && currentDisplayItem) {
                                            return <AlertCell
                                                displayItem={currentDisplayItem as IDisplayItem}
                                            />;
                                        };

                                    return null;
                                    }} 
                                    getStyleCell={(cell, cells) => {
                                        if (cell.propertyName === "lineStatus") {
                                            const value = cell.value as string;
                                        return `${changeableGridStyles.statusCell} ${changeableGridStyles[`statusCell_${value}`]}`;
                                        }
                                        return getStyleCell(cell, cells, documentType);
                                    }}
                                    renderCell={(cell, row, onDataChange, rowHeight) => {
                                        if (cell.propertyName === 'kizCategory') {
                                            const currentItem = data.items?.find(item => item.idGlobal === cell.idGlobal);
                                            
                                            if ((!currentItem?.kizs && !currentItem?.kizOsuDtos) || (currentItem?.kizs?.length === 0 && currentItem?.kizOsuDtos?.length === 0)) return (
                                                <div>
                                                    Нет КИЗ
                                                </div>
                                            );
                                            
                                            return (
                                                <KizCategorySelect
                                                    variant="grid"
                                                    currentItem={currentItem}
                                                    idGlobal={cell.idGlobal}
                                                    disabled={onlyRead}
                                                    onDataChange={onDataChange}
                                                    rowHeight={rowHeight}
                                                    containerStyle={{display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%'}}
                                                    onSwitchType={(idGlobal, newType, oldType) => {
                                                        if (oldType === KIZ_CATEGORY.medicines && newType === KIZ_CATEGORY.gisMt) {
                                                            const updatedItems = data.items.map(item => 
                                                                item.idGlobal === idGlobal
                                                                    ? { 
                                                                        ...item,
                                                                        kizOsuDtos: [...(item.kizs || []).map(kiz => ({
                                                                            ...kiz,
                                                                            goodsGroup: KizGoodsGroup.WTRU
                                                                        }))],
                                                                        kizs: [],
                                                                        isLs: false,
                                                                    }
                                                                    : item
                                                            );
                                                            setData({ ...data, items: updatedItems });
                                                        } else if (oldType === KIZ_CATEGORY.gisMt && newType === KIZ_CATEGORY.medicines) {
                                                            const updatedItems = data.items.map(item => 
                                                                item.idGlobal === idGlobal
                                                                    ? { 
                                                                        ...item, 
                                                                        kizs: [...(item.kizOsuDtos || []).map(kiz => ({
                                                                            ...kiz,
                                                                            goodsGroup: KizGoodsGroup.MDLP                                                                        
                                                                        }))],
                                                                        kizOsuDtos: [],
                                                                        isLs: true,
                                                                    }
                                                                    : item
                                                            );
                                                            setData({ ...data, items: updatedItems });
                                                        }
                                                    }}
                                                />
                                            );
                                        }
                                        return null;
                                    }}                           
                                />
                            </div>
                        </div>
                        <Accordion opened={false} caption="" id={`accompanyingInvoiceInfo-${uuidv4()}`} bold documentSticky tourRefs={invoicePricesAccordionTourRef}>
                            <GridWrapper cols={3}>
                                <MoneyFormatComponent disabled label={t("documents.invoice.averageMarkup")} inputId={"inputAccompanyingAverageMarkup"} value={+averageMarkup} /> {/*Средняя наценка*/}
                            </GridWrapper>
                            <GridWrapper cols={3}>
                                <MoneyFormatComponent disabled label={t("documents.invoice.supplierVatAmounts")} inputId={"inputAccompanyingNdsSum"} value={sum.amountSupplierVatSum} />
                                <MoneyFormatComponent
                                    disabled
                                    label={t("documents.invoice.supplierAmountsWithoutVat")}
                                    inputId={"inputAccompanyingNdsProviderSum"}
                                    value={Number(sum.amountSupplierSum)}
                                />
                                <MoneyFormatComponent disabled label={t("documents.invoice.supplierAmounts")} inputId={"inputAccompanyingProviderSum"} value={(sum.amountSupplierSumIncVat)} />
                            </GridWrapper>
                            <GridWrapper cols={3}>
                                <MoneyFormatComponent disabled label={t("documents.invoice.retailVatAmount")} inputId={"inputAccompanyingNdsRetailSumNds"} value={(sum.amountRetailVatSum)} />
                                <MoneyFormatComponent disabled label={t("documents.invoice.retailAmountWithoutVat")} inputId={"inputAccompanyingRetailSumBNds"} value={(sum.amountRetailSum)} />
                                <MoneyFormatComponent disabled label={t("documents.invoice.retailAmount")} inputId={"inputAccompanyingRetailSum"} value={(sum.amountRetailSumIncVat)} />
                            </GridWrapper>
                        </Accordion>
                    </div>
                </div>

                {showKizScan && kizScanModal}
                {recalcModal}

                {showMessageModal.show && (
                    <MessageModalWindow
                        message={showMessageModal.message}
                        ok={{
                            onClick: () => {
                                setShowMessageModal({ show: false, message: "" });
                                if (showMessageModal.btSaveOrOk === "ok") {
                                    lockingDocuments.delete({
                                        idTable: idTable,
                                        idDocument: data.idGlobal as string,
                                    });
                                    handleOk(data);
                                } else {
                                    handleSave(data);
                                }
                            },
                        }}
                        cancel={{ onClick: () => setShowMessageModal({ show: false, message: "" }) }}
                    />
                )}
                
                {showMessageModalValidDocument.show && (
                    <MessageModalWindow
                        message={showMessageModalValidDocument.message}
                        ok={{
                            onClick: () => {
                                setShowMessageModalValidDocument({ show: false, message: "" });
                            },
                        }}
                        cancel={{ onClick: () => setShowMessageModalValidDocument({ show: false, message: "" }) }}
                    />
                )}

                 {showMessageFillByGosContractGoods.show && (
                    <MessageModalWindow
                        message={showMessageFillByGosContractGoods.message}
                        ok={{
                            onClick: () => {
                                fillByGosContract(true)
                                setShowMessageFillByGosContractGoods({ show: false, message: "" })
                            },
                            title: "Да"
                        }}
                        cancel={{ 
                            onClick: () => {
                                fillByGosContract(false)
                                setShowMessageFillByGosContractGoods({ show: false, message: "" })
                            } ,
                            title: "Нет"
                        }}

                    />
                )}

                {showGosContractSelectorModal && (
                    <GosContractSearchSelectorModal 
                        ok={(entity) => {
                            setShowMessageFillByGosContractGoods({message: entity?.displayName + ': ' + baseT("fillItemByGosContractGoods"), show: true, entity: entity})
                            setShowGosContractSelectorModal(false)
                        }}
                        isExecuteAmount={true}
                        cancel={() => setShowGosContractSelectorModal(false)}
                    />
                )}

                {
                    showInvoiceItemSelectorModal && (
                        <InvoiceItemsSelectorModal 
                            idGlobalInvoice={data.idGlobal as string}
                            multipleSelect={true}
                            hasDefectMultipleSelect={true}
                            gridFilter={{
                                fieldOrderBy: "hasDefects",
                                orderDesc: true,
                                numberPerPage: 10000,
                                pageNumber: 1
                            } as IGridFilter}
                            ok={(entities) => {
                                if (entities.length == 0)  setShowInvoiceItemSelectorModal(false)

                                const request = {
                                    idInvoiceItemsGlobal: entities.map(item => item.idGlobal),
                                    IdTable: IdTableVariant.ActReturnToContractor
                                } as ICreateDocumentFromItemsRequest

                                idp.сreateDocumentFromItems(request, (response) => {
                                    tabsContext.openChild(
                                        'act_return_to_contractor_create_plugin',
                                        'edit',
                                        response.idGlobal,
                                        () => { },
                                        response?.mnemocode
                                    );
                                    setShowInvoiceItemSelectorModal(false)
                                })
                                
                            }} 
                            cancel={() => setShowInvoiceItemSelectorModal(false)}
                        />
                    )
                }

                  {showMessageModalGosContractGoods.show && (
                    <GosContractGoodsSelectorModal
                        ok={(entity) => {
                            openTab(showMessageModalGosContractGoods.entity!, 
                                {   
                                    isGosContract: true, 
                                    referentPrice: showMessageModalGosContractGoods.referentPrice,
                                    gosContractGoods: showMessageModalGosContractGoods.gosContractGoods?.find(c => c.idGlobal == entity.idGlobal) ?? null, 
                                    idGosContractGlobal: data.idGosContractExternalGlobal!
                                });
                            setShowMessageModalGosContractGoods({ show: false, message: "" })
                        }}
                        message={showMessageModalGosContractGoods.message}
                        cancel={() => setShowMessageModalGosContractGoods({ show: false, message: "" })} 
                        idGosContractGlobal={showMessageModalGosContractGoods.idGosContractGlobal!} 
                        idGoodsGlobal={showMessageModalGosContractGoods.idGoodsGlobal!}
                        />
                )}
				
            {showResultsModal && checkResults && checkResults.length > 0 && (
                <CheckKizResultsModalWindow
                    results={checkResults}
                    onRemove={handleRemoveInvalidKizs}
                    onIgnore={handleIgnoreCheck}
                    isRemoving={isRemoving}
                />
            )}

                {goodsViewItem.item && goodsViewItem.show && (
                    <InvoiceGoodsSelectModal
                        ok={(value: IEntitySimpleDTO) => {
                            callOpenInvoiceItemCreatorPlugin(value);
                        }}
                        cancel={() => setGoodsViewItem({ item: goodsViewItem.item, show: false })}
                        data={goodsViewItem.item}
                    />
                )}
                {
                    itemsByGtin.isShowModal() && data.idGlobal &&
                        <InvoiceItemsSelectorModal 
                        idGlobalInvoice={data.idGlobal as string} 
                        gridFilter={{
                            numberPerPage: 100,
                            pageNumber: 1,
                            columnFilters: [{
                                name: "gtin",
                                operator: "Eq",
                                value: itemsDocumentByGtin?.kizParsedDto?.kizBoxData?.gtin ?? itemsDocumentByGtin?.kizParsedDto?.kizData?.gtin
                             } as IGridColumnFilter]
                        } as unknown as IGridFilter}
                        ok={(entities) => {     
                            itemsByGtin.getKiz(entities.idGlobal as string,data.items, (e) => {
                                setKizByGtin(e)
                                itemsByGtin.setShowModal(false)
                            })
                        }} 
                        cancel={() => itemsByGtin.setShowModal(false)}
                />
                }

            </BaseDocumentCreator>

             {isCheckingKiz && (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 10000,
                cursor: 'wait'
            }}>
                <Spinner xl />
            </div>
            )}
        </>
    );
};

export default InvoiceCreatorForm;
